import React, { useCallback, useEffect, useMemo, useState } from "react";
import BreadCrumb from "Common/BreadCrumb";

import {
  Plus,
  Trash2,
  CheckCircle2,
  Pencil,
  Loader2,
  User2,
  Wallet,
  CalendarClock,
  Package,
  SlidersHorizontal,
  X,
} from "lucide-react";
import TableContainer from "Common/TableContainer";
import { getProformaInvoicesColumns } from "./columns";
import { useNavigate } from "react-router-dom";
import Modal from "Common/Components/Modal";
import DeleteModal from "Common/DeleteModal";
import ModalData from "Common/Components/ModalDate/ModalData";
import Select from "react-select";
import useSearchableOptions from "Common/hooks/useSearchableOptions";
import useSalesExpertNames from "Common/hooks/useSalesExpertNames";
import useCurrentRole from "Common/hooks/useCurrentRole";
import {
  canApproveSalesManager,
  canConfirmPaymentAccounting,
  canCreateProforma,
  canManageProforma,
  canRegisterPayment as canRegisterPaymentRole,
  ROLE,
} from "Common/constants/roles";
import selectStyles from "Common/constants/selectStyles";
import DatePicker, { DateObject } from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import * as Yup from "yup";
import { useFormik } from "formik";
import axios from "axios";
import {
  ltrValue,
  foamDensityWithTolerance,
  notchLabel,
  goodsCodeDescription,
} from "Common/constants/panelSpec";
import LoadingCounter from "Common/Components/LoadingCounterProps";
import ProformaPrintDocument, {
  ProformaPrintData,
} from "Common/Components/ProformaPrintDocument";
import Swal from "sweetalert2";
import {
  ORDER_STATUS_LABELS,
  ITEM_STATUS_LABELS,
  ITEM_STATUS_CLASSES,
  getItemLabel,
  isPositiveDecimalString,
  isSettled,
} from "Common/constants/proformaStatus";
import { apiErrorMessage } from "Common/constants/apiErrors";
import { accountingConfirmedAtLabel } from "Common/constants/payments";

// ========== آدرس پایه API ==========
const BASE_URL = "https://portal-api.mahanpanel.com";
const refreshAccountingMenuCounts = () =>
  window.dispatchEvent(new Event("mahan:refresh-menu-counts"));

// ========== توابع کمکی ==========
const toEnglishDigits = (str: string): string =>
  str
    .replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d).toString())
    .replace(/[٠-٩]/g, (d) => "٠١٢٣٤٥٦٧٨٩".indexOf(d).toString());

const getPersianDate = (offsetDays = 0) => {
  const d = new DateObject({ calendar: persian }).add(offsetDays, "days");
  return toEnglishDigits(d.format("YYYY/MM/DD").trim());
};

const extractList = (data: any): any[] => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

// ========== دیکد مشخصات فنی پانل ==========
const PANEL_TYPE_MAP: Record<string, string> = {
  "01": "دیواری",
  "02": "سقفی",
  "03": "پیچ مخفی",
  "04": "دیواری بدون درز",
  "05": "دیواری یک طرف بدون درز",
  "06": "سقفی یک طرف بدون درز",
  "07": "در",
};

const TOP_SHEET_MATERIAL_MAP: Record<string, string> = {
  "1": "گالوانیزه",
  "2": "آلوزینک",
  "3": "گالوالوم",
  "4": "استیل",
  "5": "آلومینیوم",
  "6": "کاغذ-کرافت",
  "7": "فویل آلومینیوم",
  "8": "سلفون",
};

const BOTTOM_SHEET_MATERIAL_MAP: Record<string, string> = {
  "1": "گالوانیزه",
  "2": "آلوزینک",
  "4": "استیل",
};

const FOAM_TYPE_MAP: Record<string, string> = {
  "1": "PUR",
  "2": "PIR",
};

const SHEET_COLOR_MAP: Record<string, string> = {
  "0": "مشکی",
  "1": "زرد",
  "2": "نارنجی",
  "3": "قرمز",
  "4": "کرم",
  "5": "آبی",
  "6": "سبز",
  "7": "طوسی",
  "9": "سفید",
};

export interface DecodedPanelSpec {
  panelType: string;
  thickness: string;
  topSheet: string;
  bottomSheet: string;
  topSheetThickness: string;
  bottomSheetThickness: string;
  foamDensity: string;
  foamType: string;
  topColor: string;
  bottomColor: string;
  description: string;
}

const decodeGoodsCode = (rawCode?: string | null): DecodedPanelSpec | null => {
  if (!rawCode) return null;
  const code = toEnglishDigits(rawCode).replace(/[^0-9]/g, "");
  if (code.length < 14) return null;

  const panelTypeDigits = code.slice(0, 2);
  const thicknessDigits = code.slice(2, 4);
  const topSheetDigit = code.slice(4, 5);
  const bottomSheetDigit = code.slice(5, 6);
  const topSheetThicknessDigits = code.slice(6, 8);
  const bottomSheetThicknessDigits = code.slice(8, 10);
  const foamDensityDigits = code.slice(10, 12);
  const foamTypeDigit = code.slice(12, 13);
  const topColorDigit = code.slice(13, 14);
  const bottomColorDigit = code.length >= 15 ? code.slice(14, 15) : "";

  const panelType = PANEL_TYPE_MAP[panelTypeDigits] || panelTypeDigits;
  const thickness = String(Number(thicknessDigits));

  return {
    panelType,
    thickness,
    topSheet: TOP_SHEET_MATERIAL_MAP[topSheetDigit] || topSheetDigit,
    bottomSheet: BOTTOM_SHEET_MATERIAL_MAP[bottomSheetDigit] || bottomSheetDigit,
    topSheetThickness: ltrValue(`${(Number(topSheetThicknessDigits) / 100).toFixed(2)} ± 0.02`),
    bottomSheetThickness: ltrValue(`${(Number(bottomSheetThicknessDigits) / 100).toFixed(2)} ± 0.02`),
    foamDensity: foamDensityWithTolerance(Number(foamDensityDigits)),
    foamType: FOAM_TYPE_MAP[foamTypeDigit] || foamTypeDigit,
    topColor: SHEET_COLOR_MAP[topColorDigit] || topColorDigit,
    bottomColor: bottomColorDigit ? SHEET_COLOR_MAP[bottomColorDigit] || bottomColorDigit : "-",
    description: `${panelType} ${thickness} سانت`,
  };
};

// برچسب‌ها و کمک‌تابع‌های وضعیت از ماژول مشترک می‌آیند تا بین صفحات سفارش یکسان بمانند.
export { ORDER_STATUS_LABELS, ITEM_STATUS_LABELS, ITEM_STATUS_CLASSES, getItemLabel };

// طبق مستند: کارشناس فروش فقط تا زمانی که سفارش در وضعیت پیش‌فاکتور است و هیچ
// واریزی تأییدشده‌ای ندارد، اجازه ویرایش/حذف دارد.
export const canSalesExpertModify = (
  invoice: Pick<ProformaInvoice, "order_status" | "confirmed_deposited_amount">
): boolean =>
  invoice.order_status === "proforma" &&
  !isPositiveDecimalString(invoice.confirmed_deposited_amount);

const formatPrice = (value: string | number) => {
  if (!value) return "";
  const num = typeof value === "string" ? parseFloat(value.replace(/,/g, "")) : value;
  if (isNaN(num)) return "";
  return num.toLocaleString("en-US");
};

const formatLiveAmount = (raw: string): string => {
  const cleaned = raw.replace(/,/g, "");
  if (cleaned === "") return "";
  const dotIndex = cleaned.indexOf(".");
  const intPart = dotIndex === -1 ? cleaned : cleaned.slice(0, dotIndex);
  const decPart = dotIndex === -1 ? "" : "." + cleaned.slice(dotIndex + 1).replace(/\./g, "");
  const intFormatted = intPart ? Number(intPart).toLocaleString("en-US") : "";
  return intFormatted + decPart;
};

const stripCommas = (v: unknown) => Number(String(v ?? "").replace(/,/g, ""));
// نمایش درصد واریزی: null/undefined → «—» ، صفر → «۰٪» ، بزرگ‌تر از ۱۰۰ یا منفی → همان‌طور
const formatDepositPercentage = (value: number | null | undefined): string => {
  if (value === null || value === undefined) return "—";
  const rounded = Math.round(Number(value));
  if (!Number.isFinite(rounded)) return "—";
  return `${rounded.toLocaleString("fa-IR")}٪`;
};
// ========== اینترفیس‌های به‌روز شده ==========
interface Customer {
  id: number;
  first_name: string;
  last_name: string | null;
  customer_type: string;
  phone_number: string;
}

const getCustomerFullName = (customer?: Customer | null) =>
  customer ? customer.first_name + (customer.last_name ? ` ${customer.last_name}` : "") : "";

interface Currency {
  id: number;
  name: string;
}

interface GoodsService {
  id: number;
  code: string;
  title: string;
  measurement_unit_name: string;
  goods_full_description?: string | null;
  display_label?: string | null;
}

interface PaymentType {
  id: number;
  name: string;
}

// ContactOut — گزینه‌های انتخابگر «رابط» از صفحه‌ی «ثبت رابط» می‌آیند.
interface Contact {
  id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  phone_number?: string | null;
}

// ===== اینترفیس آیتم (کالا) در پاسخ سرور =====
interface InvoiceItem {
  id?: number;
  line_number?: number;
  goods_service_id: number | string;
  quantity: string;
  length: string;
  width: string;
  unit_price: string;
  /**
   * «نک‌دار رو» و «نک‌دار زیر». هر کدام مستقل‌اند و پیش‌فرض هر دو true است.
   * (جایگزین فیلد قدیمی has_notch از نسخه ۳ بک‌اند.)
   */
  has_notch_top: boolean;
  has_notch_bottom: boolean;
  /**
   * فیلد منسوخ سرور. فقط برای رکوردهای قدیمی خوانده می‌شود و ارسال نمی‌گردد؛
   * سرور آن را فقط وقتی true می‌کند که هر دو نک‌دار انتخاب شده باشند.
   */
  has_notch?: boolean;
  goods_code?: string;
  deposited_percentage?: number | null;
  goods_title?: string;
  goods_description?: string;
  goods_full_description?: string | null;
  item_status?: string;
  panel_type?: string;
  panel_thickness?: number;
  top_sheet_material?: string;
  bottom_sheet_material?: string;
  top_sheet_thickness?: string;
  bottom_sheet_thickness?: string;
  foam_density?: string;
  foam_type?: string;
  top_sheet_color?: string;
  bottom_sheet_color?: string;
  area?: string;
  gross_amount?: string;
  packaging_cost?: string;
  vat_amount?: string;
  net_amount?: string;
  created_at?: string;
  created_at_jalali?: string;
  updated_at?: string;
  updated_at_jalali?: string;
  // فیلدهای اضافی که از سرور می‌آید
  [key: string]: any;
}

// ===== اینترفیس پیش‌فاکتور (با فیلدهای کامل) =====
export interface ProformaInvoice {
  id: number;
  invoice_number: number;
  order_status: "proforma" | "sales_order" | "in_production" | "produced" | "in_warehouse" | "out_of_warehouse" | "loaded" | "delivered";
  // سفارش می‌تواند فقط رابط داشته باشد، پس مشتری ممکن است خالی باشد.
  customer_id?: number | null;
  customer_name?: string | null;
  /** رابط سفارش (نسخه ۴). حداقل یکی از رابط و مشتری پر است. */
  contact_id?: number | null;
  contact_name?: string | null;
  /** درصد پورسانت رابط؛ رشته‌ی Decimal سرور. در مبلغ سفارش محاسبه نمی‌شود. */
  commission_percentage?: string | null;
  commission_amount?: string | null;
  commission_amount_display?: string | null;
  /** تأیید جداگانه‌ی پورسانت؛ از تأیید سفارش (sales_manager_approved) مستقل است. */
  commission_approved?: boolean;
  commission_approved_by_id?: number | null;
  commission_approved_by_username?: string | null;
  commission_approved_by_fullname?: string | null;
  commission_approved_at?: string | null;
  commission_approved_at_jalali?: string | null;
  invoice_date: string;
  valid_until: string;
  sale_type: "domestic" | "domestic_without_packaging";
  currency_id: number;
  currency_name: string;
  total_area?: string;
  total_length?: string;
  item_count?: number;
  gross_amount?: string;
  packaging_cost?: string;
  vat_amount?: string;
  net_amount?: string;
  deposited_amount?: string;
  confirmed_deposited_amount?: string;

  remaining_balance?: string;
  sales_manager_approved?: boolean;
  ceo_approved?: boolean;
  /**
   * تأییدکننده و زمان تأیید مدیر فروش (نسخه ۳ بک‌اند).
   * برای تأییدهای ثبت‌شده پیش از آن نسخه، هر دو null هستند و در پاسخ هم حذف می‌شوند؛
   * تنها معیار قطعیِ تأیید، خودِ sales_manager_approved است.
   */
  sales_manager_approved_by_id?: number | null;
  sales_manager_approved_by_username?: string | null;
  sales_manager_approved_by_fullname?: string | null;
  sales_manager_approved_at?: string | null;
  sales_manager_approved_at_jalali?: string | null;
  settlement_status?: boolean;
  sales_expert_username?: string;
  sales_expert_fullname?: string;
  created_by_id?: number;
  created_at?: string;
  created_at_jalali?: string;
  updated_at?: string;
  updated_at_jalali?: string;
  // فیلدهای مربوط به یک کالا (در لیست)
  goods_service_id?: number;
  goods_code?: string;
  goods_title?: string;
  quantity?: number;
  length?: string;
  width?: string;
  unit_price?: string;
  area?: string;
  goods_description?: string;
  goods_full_description?: string | null;
  item_status?: string;
  // آرایه آیتم‌ها
  items?: InvoiceItem[];
  // فیلدهای اضافی
  [key: string]: any;
}

interface Payment {
  id: number;
  proforma_invoice_id: number;
  payment_type_id: number;
  payment_type_name: string;
  amount: string;
  due_date: string;
  receipt_file_name: string | null;
  receipt_content_type: string | null;
  receipt_size: number | null;
  receipt_url: string | null;
  accounting_confirmed: boolean;
  accounting_confirmed_by_id: number | null;
  tracking_code: string | null;
  deposit_date: string | null;
  created_at: string;
  updated_at: string;
  /** زمان ثبت تأیید حسابداری (نسخه ۳). برای تأییدهای قدیمی null است. */
  accounting_confirmed_at_jalali?: string | null;
  accounting_confirmed_at?: string | null;
}

// ========== فیلترهای بالای صفحه‌های «همه سفارش‌ها» و «سایر سفارش‌ها» ==========
// ترتیب همین شیء، ترتیب نمایش فیلترها در رابط کاربری است.
export interface OrderFilters {
  customerName: string;
  /** بازه‌ی تاریخ شمسی به شکل YYYY/MM/DD (هر دو اختیاری و مستقل). */
  dateFrom: string;
  dateTo: string;
  invoiceNumber: string;
  goodsDescription: string;
  orderStatus: string;
}

/** گزینه‌های فیلتر «وضعیت سفارش»؛ از همان نگاشت مشترک برچسب‌ها ساخته می‌شود. */
const orderStatusFilterOptions = Object.entries(ORDER_STATUS_LABELS).map(
  ([value, label]) => ({ value, label: String(label) })
);

export const EMPTY_FILTERS: OrderFilters = {
  customerName: "",
  dateFrom: "",
  dateTo: "",
  invoiceNumber: "",
  goodsDescription: "",
  orderStatus: "",
};

/** ارقام فارسی/عربی به لاتین، برای اینکه ورودی با هر صفحه‌کلیدی درست ارسال شود. */
const normalizeDigits = (text: string): string =>
  String(text ?? "")
    .replace(/[۰-۹]/g, (d) => String(d.charCodeAt(0) - 0x06f0))
    .replace(/[٠-٩]/g, (d) => String(d.charCodeAt(0) - 0x0660));

/**
 * تبدیل فیلترهای فرم به پارامترهای کوئری سرور.
 *
 * فیلتر کردن کاملاً سمت سرور انجام می‌شود (نسخه ۴ بک‌اند)، پس هیچ فیلتری در
 * فرانت‌اند تکرار نمی‌شود. طبق مستند، پارامترهای خالی اصلاً ارسال نمی‌شوند؛
 * فرستادن رشته‌ی خالی باعث خطای اعتبارسنجی می‌شود.
 */
export const toFilterParams = (f: OrderFilters): Record<string, string | number> => {
  const params: Record<string, string | number> = {};
  const text = (value: string) => String(value ?? "").trim();

  if (text(f.customerName)) params.customer_name = text(f.customerName);
  if (text(f.dateFrom)) params.date_from = normalizeDigits(text(f.dateFrom));
  if (text(f.dateTo)) params.date_to = normalizeDigits(text(f.dateTo));
  // سرور invoice_number را عدد صحیح می‌خواهد؛ ورودی غیرعددی اصلاً ارسال نمی‌شود
  // تا به‌جای نتیجه‌ی خالی، خطای 422 نگیریم.
  const invoiceNumber = Number(normalizeDigits(text(f.invoiceNumber)));
  if (text(f.invoiceNumber) && Number.isInteger(invoiceNumber) && invoiceNumber > 0) {
    params.invoice_number = invoiceNumber;
  }
  if (text(f.goodsDescription)) params.goods_description = text(f.goodsDescription);
  if (f.orderStatus) params.order_status = f.orderStatus;

  return params;
};

// ========== رابط و درصد پورسانت ==========

/**
 * اعتبارسنجی «درصد پورسانت»: اختیاری، عددی بین ۰ تا ۱۰۰ و حداکثر دو رقم اعشار.
 * مقدار صفر معتبر است و نباید با خالی یکی شمرده شود، بنابراین همه‌جا با مقایسه‌ی
 * رشته‌ای بررسی می‌شود نه با truthy بودن.
 */
const commissionSchema = Yup.string()
  .notRequired()
  .test("commission-range", "درصد پورسانت باید عددی بین ۰ تا ۱۰۰ باشد", (value) => {
    if (value === undefined || value === null || String(value).trim() === "") return true;
    const numeric = Number(normalizeDigits(String(value)));
    return Number.isFinite(numeric) && numeric >= 0 && numeric <= 100;
  })
  .test("commission-decimals", "درصد پورسانت حداکثر دو رقم اعشار دارد", (value) => {
    if (value === undefined || value === null || String(value).trim() === "") return true;
    return /^\d*(\.\d{0,2})?$/.test(normalizeDigits(String(value)).trim());
  });

/**
 * مقدار پورسانت برای ارسال به سرور.
 * بدون رابط هیچ درصدی ارسال نمی‌شود (سرور آن را با ۴۲۲ رد می‌کند) و مقدار خالی
 * به null تبدیل می‌شود تا پاک شود.
 */
const commissionPayload = (
  contactId: string,
  percentage: string
): string | null => {
  if (!contactId) return null;
  const raw = normalizeDigits(String(percentage ?? "")).trim();
  if (raw === "") return null;
  const numeric = Number(raw);
  if (!Number.isFinite(numeric)) return null;
  // دو رقم اعشار، همان قالبی که سرور برمی‌گرداند.
  return numeric.toFixed(2);
};

/**
 * مبلغ پورسانت = T × R ÷ ۱۰۰
 *
 *   R: درصد پورسانت
 *   T: «جمع کل بدون مالیات بر ارزش افزوده» — همان gross_amount در پاسخ سفارش که
 *      تست شد دقیقاً برابر totals.grand_total در چاپ پیش‌فاکتور است.
 *
 * اگر درصد یا مبلغ پایه مشخص نباشد null برمی‌گردد تا فراخوان «-» نشان دهد.
 * درصد صفر معتبر است و صفر برمی‌گرداند، نه null.
 */
export const commissionAmount = (
  grossAmount: unknown,
  percentage: unknown
): number | null => {
  const raw = String(percentage ?? "").trim();
  if (raw === "") return null;
  const rate = Number(normalizeDigits(raw));
  const rawBase = String(grossAmount ?? "").trim();
  // رشته‌ی خالی را Number به صفر تبدیل می‌کند؛ پس صریح رد می‌شود تا «مبلغ نامشخص»
  // با «مبلغ صفر» اشتباه نشود.
  if (rawBase === "") return null;
  const base = Number(normalizeDigits(rawBase));
  if (!Number.isFinite(rate) || !Number.isFinite(base)) return null;
  return (base * rate) / 100;
};

/** مبلغ با جداکننده‌ی سه‌رقمی؛ برای نمایش «مبلغ پورسانت». */
export const formatCommissionAmount = (value: number | null): string =>
  value === null ? "-" : Math.round(value).toLocaleString("en-US");

/** نام تأییدکننده‌ی پورسانت: اول نام کامل، در نبودش نام کاربری. */
const commissionApproverName = (invoice?: ProformaInvoice | null): string =>
  (invoice?.commission_approved_by_fullname || "").trim() ||
  invoice?.commission_approved_by_username ||
  "";

export interface ProformaInvoicesPageProps {
  /** عنوان صفحه و مسیر بالای صفحه */
  pageTitle?: string;
  /** برچسب شمارنده بالای جدول */
  totalLabel?: string;
  /** متن حالت خالی بودن فهرست */
  emptyText?: string;
  /** آیا دکمه «ایجاد پیش‌فاکتور» نمایش داده شود */
  showCreateButton?: boolean;
  /**
   * فیلتر order_status. پیش‌فرض "proforma" است تا صفحه‌ی پیش‌فاکتورها فقط رکوردهایی را
   * نشان دهد که هنوز در وضعیت پیش‌فاکتورند؛ به‌محض تبدیل به سفارش فروش، رکورد از این
   * فهرست خارج و در «سفارش‌های فروش» دیده می‌شود. مقدار null یعنی بدون فیلتر (همه سفارش‌ها).
   */
  orderStatus?: string | null;
  /**
   * این نمونه کدام بخش منو است. مستند هر عملیات را به یک بخش گره زده:
   * «ثبت/ویرایش/حذف پیش‌فاکتور»، «ثبت پرداختی» و «تأیید مدیر فروش» متعلق به بخش
   * پیش‌فاکتورها هستند و «تأیید پرداختی» متعلق به بخش همه سفارش‌ها.
   */
  section?: "proformaInvoices" | "allOrders" | "otherOrders";
  /** تعداد ستون نمایشی در موبایل. پیش‌فرض ۳ (مقدار پیش‌فرض جدول). */
  mobileColumns?: number;
  /**
   * تعداد ستون نمایشی روی دسکتاپ. بقیه‌ی ستون‌ها پنهان می‌شوند و با دکمه‌ی + هر
   * ردیف باز می‌شوند. پیش‌فرض ۸.
   */
  tableColumns?: number;
  /**
   * نوار فیلترهای بالای صفحه: نام مشتری، بازه‌ی تاریخ، شماره فاکتور، شرح کالا و
   * وضعیت سفارش. همه‌ی این‌ها پارامتر سروری دارند و فیلتر سمت سرور انجام می‌شود.
   */
  showFilters?: boolean;
  /**
   * اندپوینت فهرست. پیش‌فرض «/proforma-invoices/» است؛ صفحه‌ی «سایر سفارش‌ها» از
   * «/proforma-invoices/other-orders/» استفاده می‌کند که سفارش‌های همه‌ی کارشناسان
   * فروش را برمی‌گرداند.
   */
  listEndpoint?: string;
  /** درج ستون «تاریخ» بلافاصله بعد از «کارشناس فروش». */
  showDateColumn?: boolean;
  /**
   * فقط‌خواندنی. همه‌ی عملیات (ایجاد، ویرایش، حذف، ثبت/تأیید پرداخت، تأیید مدیر
   * فروش) غیرفعال می‌شود و فقط مشاهده باقی می‌ماند.
   */
  readOnly?: boolean;
  /** فیلتر مستقل از مرحله سفارش برای صف‌های حسابداری نسخه ۶. */
  paymentConfirmation?: "pending" | "confirmed";
}

// این صفحه فهرست کامل GET /proforma-invoices/ را بدون فیلتر order_status می‌گیرد، یعنی
// پیش‌فاکتورها را در هر وضعیتی نشان می‌دهد. صفحه «همه سفارش‌ها» همین کامپوننت است با
// عنوان‌های متفاوت (نگاه کنید به pages/AllOrders).
const Index = ({
  pageTitle = "پیش‌فاکتورها",
  totalLabel = "مجموع پیش‌فاکتورها",
  emptyText = "هیچ پیش‌فاکتوری ثبت نشده است",
  showCreateButton = true,
  orderStatus = "proforma",
  section = "proformaInvoices",
  mobileColumns,
  tableColumns = 8,
  showFilters = false,
  listEndpoint = "/proforma-invoices/",
  showDateColumn = false,
  readOnly = false,
  paymentConfirmation,
}: ProformaInvoicesPageProps) => {
  const navigate = useNavigate();
  const { roleId: currentUserRoleId, isAdmin: isAdminRoleId, salesEnabled } = useCurrentRole();
  // در حالت فقط‌خواندنی هیچ نقشی مجوز عملیات نمی‌گیرد؛ همین یک متغیر همه‌ی دکمه‌های
  // ویرایش/حذف/پرداخت/تأیید را در ستون‌ها و مودال‌ها خاموش می‌کند.
  const isAdmin = isAdminRoleId && !readOnly;
  const onProformaSection = section === "proformaInvoices";
  // ویرایش/حذف پیش‌فاکتور: کارشناس فروش، فقط در بخش پیش‌فاکتورها.
  // (ثبت پرداخت از این پرچم جدا شد — نگاه کنید به mayRegisterPayment در بند ۸.)
  const isSalesExpert =
    canManageProforma(currentUserRoleId) && !isAdmin && onProformaSection && !readOnly;
  // تأیید مدیر فروش: مدیر فروش، فقط در بخش پیش‌فاکتورها.
  const isSalesManager =
    canApproveSalesManager(currentUserRoleId) && !isAdmin && onProformaSection && !readOnly;
  // ===== بند ۸ مستند نسخه ۵ =====
  // «ثبت پرداخت» برای کارشناس فروش و «تأیید پرداخت» برای کارشناس حسابداری باید
  // همیشه فعال باشند — سفارش هر تغییر وضعیتی داشته باشد فرقی نمی‌کند. تنها
  // بازدارنده، تسویه‌شدن سفارش است. برخلاف isSalesExpert/isAccountingExpert که
  // به بخش گره خورده‌اند، این دو فقط به نقش نگاه می‌کنند.
  const mayRegisterPayment =
    !readOnly && (isAdmin || canRegisterPaymentRole(currentUserRoleId));
  const mayConfirmAccounting =
    !readOnly && (isAdmin || canConfirmPaymentAccounting(currentUserRoleId));

  // ایجاد پیش‌فاکتور: کارشناس فروش و مدیر فروش (مدیر فروش اجازه‌ی ویرایش/حذف ندارد).
  const mayCreateProforma =
    showCreateButton && canCreateProforma(currentUserRoleId) && onProformaSection && !readOnly;
  const salesCreationBlocked = currentUserRoleId === ROLE.SALES_EXPERT && salesEnabled === false;
  const mayCreateProformaNow = mayCreateProforma && !salesCreationBlocked;

  const { salesExpertNames, getSalesExpertName, seedFromRows } = useSalesExpertNames();

  // ========== چاپ پیش‌فاکتور ==========
  // سند چاپ به داده‌ی کامل نیاز دارد: اقلام از جزئیات پیش‌فاکتور و مشخصات خریدار
  // از رکورد مشتری. هر دو هنگام زدن دکمه گرفته می‌شوند.
  // کل داده‌ی چاپ از یک درخواست واحد print-data می‌آید.
  const [printData, setPrintData] = useState<ProformaPrintData | null>(null);
  const [printLoading, setPrintLoading] = useState<boolean>(false);

  const handlePrint = useCallback(
    async (invoice: ProformaInvoice) => {
      if (printLoading) return;
      setPrintLoading(true);
      try {
        const token =
          localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");

        // تمام داده‌ی چاپ (فاکتور، خریدار، فروشنده، توضیحات و شرایط فروش) با همین
        // یک درخواست می‌آید؛ دیگر درخواست جداگانه‌ای برای مشتری ارسال نمی‌شود.
        const res = await axios.get(
          BASE_URL + "/proforma-invoices/" + invoice.id + "/print-data",
          { headers: { Authorization: "Bearer " + token } }
        );

        // از نسخه ۳، خودِ سرور invoice_time (قالب HH:MM به وقت تهران) را می‌فرستد؛
        // دیگر لازم نیست ساعت از created_at_jalali بیرون کشیده شود.
        setPrintData(res.data);

        // دو فریم صبر می‌کنیم تا سند چاپ واقعاً در DOM رندر شده باشد.
        await new Promise<void>((resolve) => {
          window.requestAnimationFrame(() =>
            window.requestAnimationFrame(() => resolve())
          );
        });

        window.print();
      } catch (error: any) {
        console.error("Error preparing print:", error);
        const status = error.response?.status;
        if (status === 401) {
          navigate("/login");
          return;
        }
        if (status === 403) {
          Swal.fire("خطا", apiErrorMessage(error, "شما مجوز انجام این عملیات را ندارید"), "error");
          return;
        }
        if (status === 404) {
          Swal.fire("خطا", apiErrorMessage(error, "این پیش‌فاکتور یافت نشد یا حذف شده است"), "error");
          return;
        }
        Swal.fire("خطا", apiErrorMessage(error, "دریافت اطلاعات برای چاپ ممکن نشد"), "error");
      } finally {
        setPrintLoading(false);
      }
    },
    [navigate, printLoading]
  );

  // State
  const [invoices, setInvoices] = useState<ProformaInvoice[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [currencies, setCurrencies] = useState<Currency[]>([]);
  const defaultCurrencyId = useMemo(() => {
    const rial = currencies.find((c) => c.name?.trim() === "ریال");
    return rial ? String(rial.id) : "";
  }, [currencies]);
  const [goods, setGoods] = useState<GoodsService[]>([]);
  const [paymentTypes, setPaymentTypes] = useState<PaymentType[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [reload, setReload] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const [totalItems, setTotalItems] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [sortBy, setSortBy] = useState<string | undefined>();
  const [sortOrder, setSortOrder] = useState<"default" | "asc" | "desc">("default");
  const changeServerSort = useCallback((nextSortBy: string | undefined, nextSortOrder: "default" | "asc" | "desc") => {
    setSortBy(nextSortBy);
    setSortOrder(nextSortOrder);
    setCurrentPage(1);
  }, []);

  // ===== فیلترهای بالای صفحه (فقط وقتی showFilters روشن است) =====
  // `draftFilters` مقدارِ در حال ویرایش در مودال است و `filters` مقدار اعمال‌شده که
  // به سرور فرستاده می‌شود؛ جدا نگه داشتن‌شان از درخواست‌زدن با هر ضربه‌ی کیبورد
  // جلوگیری می‌کند.
  const [showFilterModal, setShowFilterModal] = useState<boolean>(false);
  const [filters, setFilters] = useState<OrderFilters>(EMPTY_FILTERS);
  const [draftFilters, setDraftFilters] = useState<OrderFilters>(EMPTY_FILTERS);
  // پارامترهای فیلتر در یک رشته سریالایز می‌شوند تا وابستگی effect پایدار بماند.
  const filterParams = useMemo(() => toFilterParams(filters), [filters]);
  const filterKey = JSON.stringify(filterParams);

  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showCreateConfirm, setShowCreateConfirm] = useState<boolean>(false);
  const [createSubmitting, setCreateSubmitting] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  // رکورد کاملِ در حال ویرایش؛ وضعیت تأیید پورسانت از همین خوانده می‌شود، نه از
  // مقادیر فرم، چون تأیید مستقل از ذخیره‌ی فرم انجام می‌شود.
  const [editingInvoice, setEditingInvoice] = useState<ProformaInvoice | null>(null);
  const [itemSubmittingIndex, setItemSubmittingIndex] = useState<number | null>(null);
  const [showSpecsModal, setShowSpecsModal] = useState<ProformaInvoice | null>(null);
  const [showItemDetailModal, setShowItemDetailModal] = useState<{
    invoice: ProformaInvoice;
    item: InvoiceItem;
    index: number;
  } | null>(null);
  const [showPaymentsModal, setShowPaymentsModal] = useState<number | null>(null);
  // رکورد سفارشی که مودال پرداخت‌هایش باز است؛ برای بررسی «تسویه شده» لازم است
  // (بند ۸ مستند نسخه ۵). showPaymentsModal فقط شناسه را نگه می‌دارد.
  const paymentsInvoice = useMemo(
    () => invoices.find((invoice) => invoice.id === showPaymentsModal) || null,
    [invoices, showPaymentsModal]
  );
  const [payments, setPayments] = useState<Payment[]>([]);
  const [paymentsLoading, setPaymentsLoading] = useState<boolean>(false);
  const [receiptPreview, setReceiptPreview] = useState<{ url: string; name: string } | null>(null);
  const [showRegisterPayment, setShowRegisterPayment] = useState<number | null>(null);
  const [paymentSubmitting, setPaymentSubmitting] = useState<boolean>(false);
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
  const [specsLoading, setSpecsLoading] = useState<boolean>(false);
  const [approvalTarget, setApprovalTarget] = useState<{ invoiceId: number; paymentId: number } | null>(null);
  const [approvalStep, setApprovalStep] = useState<"confirm" | "form">("confirm");
  const [approvalForm, setApprovalForm] = useState({ tracking_code: "", deposit_date: "" });

  const [paymentForm, setPaymentForm] = useState({
    payment_type_id: "",
    amount: "",
    due_date: getPersianDate(0),
    receipt_file: null as File | null,
    tracking_code: "",
    deposit_date: getPersianDate(0),
  });

  const [editPaymentTarget, setEditPaymentTarget] = useState<Payment | null>(null);
  const [editPaymentSubmitting, setEditPaymentSubmitting] = useState<boolean>(false);
  const [editPaymentForm, setEditPaymentForm] = useState({
    payment_type_id: "",
    amount: "",
    due_date: getPersianDate(0),
    receipt_file: null as File | null,
    accounting_confirmed: false,
    tracking_code: "",
    deposit_date: getPersianDate(0),
  });

  // ========== دریافت داده‌های اولیه ==========
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
        const headers = { Authorization: `Bearer ${token}` };

        const [customersRes, currenciesRes, goodsRes, paymentTypesRes, contactsRes] =
          await Promise.all([
            axios.get(`${BASE_URL}/customers/`, { headers }),
            axios.get(`${BASE_URL}/currencies/`, { headers }),
            axios.get(`${BASE_URL}/goods-services/`, { headers }),
            axios.get(`${BASE_URL}/payment-types/`, { headers }),
            axios.get(`${BASE_URL}/contacts/`, { headers }),
          ]);

        setCustomers(extractList(customersRes.data));
        setContacts(extractList(contactsRes.data));
        setCurrencies(extractList(currenciesRes.data));
        setGoods(extractList(goodsRes.data));
        // انواع پرداخت صعودی بر اساس شناسه مرتب می‌شوند تا نوع پرداختِ
        // تازه‌ثبت‌شده انتهای فهرست کشویی «ثبت پرداخت» نمایش داده شود.
        setPaymentTypes(
          [...extractList(paymentTypesRes.data)].sort(
            (a: any, b: any) => Number(a?.id ?? 0) - Number(b?.id ?? 0)
          )
        );
      } catch (error: any) {
        console.error("Error fetching initial data:", error);
        if (error.response?.status === 401) {
          navigate("/login");
        }
      }
    };
    fetchInitialData();
  }, [navigate]);

  // خروجی اکسل باید همه‌ی رکوردهای منطبق بر فیلترهای فعلی را بدهد، نه فقط
  // صفحه‌ی جاری؛ پس همان اندپوینت و همان فیلترها با size=100 صفحه‌به‌صفحه خوانده
  // می‌شوند تا انتها.
  const fetchAllInvoices = useCallback(async () => {
    const token =
      localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
    const collected: ProformaInvoice[] = [];
    let page = 1;
    let pages = 1;
    do {
      const res = await axios.get(`${BASE_URL}${listEndpoint}`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page,
          size: 100, // سقف مجاز سرور
          ...(orderStatus ? { order_status: orderStatus } : {}),
          ...(paymentConfirmation ? { payment_confirmation: paymentConfirmation } : {}),
          ...filterParams,
          ...(sortOrder === "default" ? { sort_order: "default" } : { sort_by: sortBy, sort_order: sortOrder }),
        },
      });
      collected.push(...extractList(res.data));
      pages = res.data?.pages || 1;
      page += 1;
    } while (page <= pages && page <= 50); // سقف ایمنی
    return collected;
    // filterKey به‌جای شیء filterParams در وابستگی‌ها می‌آید تا با هر رندر تازه نشود.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderStatus, paymentConfirmation, listEndpoint, filterKey, sortBy, sortOrder]);

  // ========== دریافت لیست پیش‌فاکتورها ==========
  // فیلتر و صفحه‌بندی هر دو سمت سرور انجام می‌شوند (نسخه ۴ بک‌اند)؛ هیچ فیلتری
  // در فرانت‌اند تکرار نمی‌شود تا شمارنده و صفحه‌بندی همیشه با سرور یکی بماند.
  useEffect(() => {
    const fetchInvoices = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
        const res = await axios.get(`${BASE_URL}${listEndpoint}`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            page: currentPage,
            size: pageSize,
            ...(orderStatus ? { order_status: orderStatus } : {}),
            ...(paymentConfirmation ? { payment_confirmation: paymentConfirmation } : {}),
            ...filterParams,
            ...(sortOrder === "default" ? { sort_order: "default" } : { sort_by: sortBy, sort_order: sortOrder }),
          },
        });

        const data = extractList(res.data);
        setInvoices(data);
        // اگر پاسخ فهرست خودش نام کامل کارشناس را داشته باشد، وارد نگاشت می‌شود تا نقش‌هایی
        // که اجازه‌ی GET /users/ ندارند هم نام کامل را ببینند.
        seedFromRows(data);
        // `total` و `pages` از پاسخ صفحه‌بندی‌شده سرور می‌آیند، نه از تعداد ردیف‌های همین صفحه.
        setTotalItems(res.data?.total ?? data.length);
        setTotalPages(res.data?.pages ?? 1);
        setErrorMessage("");
      } catch (error: any) {
        console.error("Error fetching invoices:", error);
        if (error.response?.status === 401) {
          navigate("/login");
          return;
        }
        // خطای ۴۲۲ اینجا یعنی فیلتر نامعتبر (مثلاً تاریخ شروع بعد از پایان)؛ متن
        // فارسی خود سرور نمایش داده می‌شود تا کاربر بداند کدام فیلتر را اصلاح کند.
        setInvoices([]);
        setTotalItems(0);
        setTotalPages(1);
        setErrorMessage(
          apiErrorMessage(error, "مشکلی در دریافت لیست پیش‌فاکتورها رخ داده است.")
        );
      } finally {
        setLoading(false);
      }
    };
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload, currentPage, pageSize, orderStatus, paymentConfirmation, listEndpoint, filterKey, navigate, seedFromRows, sortBy, sortOrder]);

  // با تغییر فیلترها باید به صفحه‌ی اول برگردیم، وگرنه ممکن است روی صفحه‌ای بمانیم
  // که در نتیجه‌ی جدید وجود ندارد و جدول خالی دیده شود.
  useEffect(() => {
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterKey]);

  const exportRows = fetchAllInvoices;
  const activeFilterCount = useMemo(
    () => Object.values(filters).filter((value) => String(value).trim() !== "").length,
    [filters]
  );

  // ========== دریافت پرداخت‌ها ==========
  const fetchPayments = useCallback(
    async (invoiceId: number) => {
      setPaymentsLoading(true);
      try {
        const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
        const res = await axios.get(`${BASE_URL}/proforma-invoices/${invoiceId}/payments`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setPayments(extractList(res.data));
      } catch (error: any) {
        console.error("Error fetching payments:", error);
        if (error.response?.status === 401) {
          navigate("/login");
        }
        Swal.fire("خطا", apiErrorMessage(error, "مشکلی در دریافت پرداخت‌ها رخ داده است"), "error");
      } finally {
        setPaymentsLoading(false);
      }
    },
    [navigate]
  );

  // ========== حذف پیش‌فاکتور ==========
  const handleDeleteInvoice = async () => {
    if (!deleteTargetId) return;
    try {
      const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
      const res = await axios.delete(`${BASE_URL}/proforma-invoices/${deleteTargetId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 200) {
        Swal.fire("عملیات موفق", "پیش‌فاکتور با موفقیت حذف شد", "success");
        setReload(!reload);
        setDeleteTargetId(null);
      }
    } catch (error: any) {
      console.error("Delete error:", error);
      if (error.response?.status === 401) {
        navigate("/login");
        return;
      }
      if (error.response?.status === 409) {
        Swal.fire("خطا", apiErrorMessage(error, "به علت وجود واریزی تأییدشده یا تغییر وضعیت، امکان حذف وجود ندارد."), "error");
        return;
      }
      const msg = apiErrorMessage(error, "خطا در حذف پیش‌فاکتور");
      Swal.fire("خطا", msg, "error");
    }
  };

  // ========== فرم ایجاد ==========
  const itemValidationSchema = Yup.object({
    goods_service_id: Yup.string().required("لطفاً کالا/خدمت را انتخاب کنید"),
    quantity: Yup.string().required("لطفاً تعداد را وارد کنید"),
    length: Yup.string().required("لطفاً طول را وارد کنید"),
    width: Yup.string().required("لطفاً عرض را وارد کنید"),
    unit_price: Yup.string().required("لطفاً قیمت واحد را وارد کنید"),
  });

  const createValidation = useFormik({
    enableReinitialize: true,
    initialValues: {
      // ترتیب مستند: رابط، مشتری، درصد پورسانت.
      contact_id: "",
      customer_id: "",
      commission_percentage: "",
      invoice_date: getPersianDate(0),
      valid_until: getPersianDate(1),
      sale_type: "domestic" as "domestic" | "domestic_without_packaging",
      currency_id: defaultCurrencyId,
      items: [
        { goods_service_id: "", quantity: "", length: "", width: "", unit_price: "", has_notch_top: true, has_notch_bottom: true },
      ] as InvoiceItem[],
    },
    validationSchema: Yup.object({
      // رابط و مشتری هر دو اختیاری‌اند ولی حداقل یکی باید پر باشد. خطا روی فیلد
      // «مشتری» می‌نشیند چون آخرین فیلد این جفت است و کاربر آن را می‌بیند.
      contact_id: Yup.string().notRequired(),
      customer_id: Yup.string().test(
        "contact-or-customer",
        "پر کردن حداقل یکی از فیلدهای رابط یا مشتری الزامی است.",
        function (value) {
          return Boolean(value || this.parent.contact_id);
        }
      ),
      commission_percentage: commissionSchema,
      invoice_date: Yup.string().required("لطفاً تاریخ را انتخاب کنید"),
      valid_until: Yup.string().required("لطفاً تاریخ اعتبار را انتخاب کنید"),
      sale_type: Yup.string().required("لطفاً نوع فروش را انتخاب کنید"),
      currency_id: Yup.string().required("لطفاً ارز را انتخاب کنید"),
      items: Yup.array()
        .of(itemValidationSchema)
        .min(1, "حداقل یک کالا/خدمت باید افزوده شود"),
    }),
    onSubmit: async () => {
      setShowCreateConfirm(true);
    },
  });

  // ========== ثبت نهایی پیش‌فاکتور (فعال شده) ==========
  const submitCreateInvoice = async () => {
    if (createSubmitting) return;
    const values = createValidation.values;

    const errors = await createValidation.validateForm();
    if (Object.keys(errors).length > 0) {
      createValidation.setTouched({
        contact_id: true,
        customer_id: true,
        commission_percentage: true,
        invoice_date: true,
        valid_until: true,
        sale_type: true,
        currency_id: true,
        items: values.items.map(() => ({})),
      });
      Swal.fire("خطا", "لطفاً تمام فیلدهای ضروری را پر کنید.", "error");
      return;
    }

    if (values.items.length === 0) {
      Swal.fire("خطا", "حداقل یک کالا/خدمت باید افزوده شود.", "error");
      return;
    }

    const payload = {
      // طرف سفارش: هرکدام که انتخاب نشده باشد null می‌رود، نه صفر و نه رشته‌ی خالی.
      contact_id: values.contact_id ? Number(values.contact_id) : null,
      customer_id: values.customer_id ? Number(values.customer_id) : null,
      commission_percentage: commissionPayload(values.contact_id, values.commission_percentage),
      invoice_date: values.invoice_date,
      valid_until: values.valid_until,
      sale_type: values.sale_type,
      currency_id: Number(values.currency_id),
      items: values.items.map((item) => ({
        goods_service_id: Number(item.goods_service_id),
        quantity: Number(stripCommas(item.quantity)),
        length: Number(stripCommas(item.length)),
        width: Number(stripCommas(item.width)),
        unit_price: parseFloat(item.unit_price.replace(/,/g, "")) || 0,
        // این دو در ثبت اولیه هم باید بروند، وگرنه سرور هر دو را true می‌گیرد.
        has_notch_top: item.has_notch_top ?? true,
        has_notch_bottom: item.has_notch_bottom ?? true,
      })),
    };

    setCreateSubmitting(true);
    try {
      const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
      const res = await axios.post(`${BASE_URL}/proforma-invoices/`, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.status === 201 || res.status === 200) {
        Swal.fire("عملیات موفق", "پیش‌فاکتور با موفقیت ثبت شد", "success");
        setReload(!reload);
        setShowCreateConfirm(false);
        setShowCreateModal(false);
        createValidation.resetForm();
      }
    } catch (error: any) {
      console.error("Create error:", error);
      if (error.response?.status === 401) {
        navigate("/login");
        return;
      }
      if (error.response?.status === 409) {
        Swal.fire("خطا", apiErrorMessage(error, "امکان ثبت پیش‌فاکتور وجود ندارد (ممکن است مبلغ واریزی کافی نباشد)."), "error");
        return;
      }
      if (error.response?.status === 422) {
        // پیغام خطای سرور انگلیسی است؛ apiErrorMessage آن را به جمله‌ی فارسی
        // گویا تبدیل می‌کند (تکراری بودن کد ملی/شماره همراه، فیلد الزامی و ...).
        let msg = apiErrorMessage(error, "اطلاعات وارد شده نامعتبر است");
        Swal.fire("خطا", msg, "error");
      } else {
        Swal.fire("خطا", apiErrorMessage(error, "مشکل در ثبت پیش‌فاکتور"), "error");
      }
    } finally {
      setCreateSubmitting(false);
    }
  };

  // توابع مدیریت آیتم‌ها در فرم ایجاد
  const addCreateItem = () => {
    createValidation.setFieldValue("items", [
      ...createValidation.values.items,
      { goods_service_id: "", quantity: "", length: "", width: "", unit_price: "", has_notch_top: true, has_notch_bottom: true },
    ]);
  };

  const removeCreateItem = (index: number) => {
    const newItems = [...createValidation.values.items];
    newItems.splice(index, 1);
    createValidation.setFieldValue("items", newItems);
  };

  // مقدار می‌تواند boolean باشد (چک‌باکس «نک‌دار»)، پس امضای setter هر دو را می‌پذیرد.
  const updateCreateItem = (
    index: number,
    field: keyof InvoiceItem,
    value: string | boolean
  ) => {
    const newItems = [...createValidation.values.items];
    newItems[index] = { ...newItems[index], [field]: value };
    createValidation.setFieldValue("items", newItems);
  };

  // ========== فرم ویرایش ==========
  const editValidation = useFormik({
    enableReinitialize: true,
    initialValues: {
      contact_id: "",
      customer_id: "",
      commission_percentage: "",
      invoice_date: getPersianDate(0),
      valid_until: getPersianDate(1),
      sale_type: "domestic" as "domestic" | "domestic_without_packaging",
      currency_id: "",
      items: [] as InvoiceItem[],
    },
    validationSchema: Yup.object({
      contact_id: Yup.string().notRequired(),
      customer_id: Yup.string().test(
        "contact-or-customer",
        "پر کردن حداقل یکی از فیلدهای رابط یا مشتری الزامی است.",
        function (value) {
          return Boolean(value || this.parent.contact_id);
        }
      ),
      commission_percentage: commissionSchema,
      invoice_date: Yup.string().required("لطفاً تاریخ را انتخاب کنید"),
      valid_until: Yup.string().required("لطفاً تاریخ اعتبار را انتخاب کنید"),
      sale_type: Yup.string().required("لطفاً نوع فروش را انتخاب کنید"),
      currency_id: Yup.string().required("لطفاً ارز را انتخاب کنید"),
      // اقلام در این فرم اعتبارسنجی نمی‌شوند چون جداگانه و مستقیم روی سرور ثبت می‌شوند.
    }),
    onSubmit: async (values) => {
      if (!editingId) return;
      // ProformaInvoiceUpdate فقط همین پنج فیلد سربرگ را می‌پذیرد؛ اقلام از طریق
      // اندپوینت‌های اختصاصی items مدیریت می‌شوند.
      // فرستادن null یعنی پاک‌کردن آن فیلد؛ هر دو طرف هم‌زمان خالی نمی‌شوند چون
      // اعتبارسنجی بالا جلویش را می‌گیرد.
      const payload = {
        contact_id: values.contact_id ? Number(values.contact_id) : null,
        customer_id: values.customer_id ? Number(values.customer_id) : null,
        commission_percentage: commissionPayload(
          values.contact_id,
          values.commission_percentage
        ),
        invoice_date: values.invoice_date,
        valid_until: values.valid_until,
        sale_type: values.sale_type,
        currency_id: Number(values.currency_id),
      };

      try {
        const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
        const res = await axios.patch(`${BASE_URL}/proforma-invoices/${editingId}`, payload, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });
        if (res.status === 200) {
          Swal.fire("عملیات موفق", "پیش‌فاکتور با موفقیت ویرایش شد", "success");
          setReload(!reload);
          setShowEditModal(false);
          setEditingId(null);
          editValidation.resetForm();
        }
      } catch (error: any) {
        console.error("Update error:", error);
        if (error.response?.status === 401) {
          navigate("/login");
          return;
        }
        if (error.response?.status === 409) {
          Swal.fire("خطا", apiErrorMessage(error, "این پیش‌فاکتور قابل ویرایش نیست (توسط حسابداری قفل شده است)"), "error");
        } else if (error.response?.status === 422) {
          // پیغام خطای سرور انگلیسی است؛ apiErrorMessage آن را به جمله‌ی فارسی
          // گویا تبدیل می‌کند (تکراری بودن کد ملی/شماره همراه، فیلد الزامی و ...).
          let msg = apiErrorMessage(error, "اطلاعات وارد شده نامعتبر است");
          Swal.fire("خطا", msg, "error");
        } else {
          Swal.fire("خطا", apiErrorMessage(error, "مشکل در ویرایش پیش‌فاکتور"), "error");
        }
      }
    },
  });

  const addEditItem = () => {
    editValidation.setFieldValue("items", [
      ...editValidation.values.items,
      { goods_service_id: "", quantity: "", length: "", width: "", unit_price: "", has_notch_top: true, has_notch_bottom: true },
    ]);
  };

  const removeEditItem = (index: number) => {
    const newItems = [...editValidation.values.items];
    newItems.splice(index, 1);
    editValidation.setFieldValue("items", newItems);
  };

  const updateEditItem = (
    index: number,
    field: keyof InvoiceItem,
    value: string | boolean
  ) => {
    const newItems = [...editValidation.values.items];
    newItems[index] = { ...newItems[index], [field]: value };
    editValidation.setFieldValue("items", newItems);
  };

  const fetchInvoiceDetail = async (invoiceId: number): Promise<ProformaInvoice | null> => {
    try {
      const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
      // در «سایر سفارش‌ها» جزئیات از اندپوینت فقط‌خواندنی خوانده می‌شود، وگرنه برای
      // سفارش کارشناسِ دیگر خطای 403 می‌گیریم.
      const detailUrl = readOnly
        ? `${BASE_URL}/proforma-invoices/other-orders/${invoiceId}`
        : `${BASE_URL}/proforma-invoices/${invoiceId}`;
      const res = await axios.get(detailUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return res.data;
    } catch (error: any) {
      console.error("Error fetching invoice detail:", error);
      if (error.response?.status === 401) {
        navigate("/login");
      }
      return null;
    }
  };

  // اقلام سرور را به شکل قابل ویرایش فرم درمی‌آورد و شناسه‌ها را نگه می‌دارد
  // (طبق مستند، دکمه‌های ویرایش/حذف قلم باید invoice_id و item_id را هم‌زمان داشته باشند).
  const toEditItems = (serverItems?: InvoiceItem[]): InvoiceItem[] =>
    (serverItems || []).map((item) => ({
      id: item.id,
      line_number: item.line_number,
      item_status: item.item_status,
      goods_service_id: String(item.goods_service_id ?? ""),
      quantity: formatPrice(item.quantity),
      length: formatPrice(item.length),
      width: formatPrice(item.width),
      unit_price: formatPrice(item.unit_price),
      // رکوردهای قدیمی فقط has_notch دارند؛ همان مقدار به هر دو فیلد جدید می‌نشیند.
      has_notch_top: item.has_notch_top ?? item.has_notch ?? true,
      has_notch_bottom: item.has_notch_bottom ?? item.has_notch ?? true,
    }));

  // پس از هر عملیات قلم، پاسخ سرور مبنا قرار می‌گیرد (هیچ به‌روزرسانی خوش‌بینانه‌ای انجام نمی‌شود).
  const refreshAfterItemChange = async (invoiceId: number) => {
    const detail = await fetchInvoiceDetail(invoiceId);
    if (detail) {
      editValidation.setFieldValue("items", toEditItems(detail.items));
      setShowSpecsModal((prev) => (prev && prev.id === invoiceId ? detail : prev));
    }
    setReload((prev) => !prev);
  };

  const itemApiErrorMessage = (error: any, fallback: string) => {
    const status = error.response?.status;
    if (status === 409) {
      return (
        apiErrorMessage(error, "امکان انجام این عملیات وجود ندارد؛ حذف آخرین قلم سفارش مجاز نیست یا وضعیت سفارش تغییر کرده است.")
      );
    }
    if (status === 403) return "شما مجوز انجام این عملیات را ندارید.";
    if (status === 404) return "این قلم یافت نشد؛ ممکن است قبلاً حذف شده باشد.";
    // بقیه‌ی خطاها (اعتبارسنجی، تکراری بودن، ...) به فارسی ترجمه می‌شوند.
    return apiErrorMessage(error, fallback);
  };

  const buildItemPayload = (item: InvoiceItem) => ({
    goods_service_id: Number(item.goods_service_id),
    quantity: stripCommas(item.quantity),
    length: stripCommas(item.length),
    width: stripCommas(item.width),
    unit_price: stripCommas(item.unit_price),
    // عمداً از ?? استفاده می‌شود نه ||، وگرنه false به true تبدیل می‌شود.
    // فیلد منسوخ has_notch ارسال نمی‌شود؛ سرور خودش آن را از این دو می‌سازد.
    has_notch_top: item.has_notch_top ?? true,
    has_notch_bottom: item.has_notch_bottom ?? true,
  });

  const validateItemPayload = (item: InvoiceItem): string | null => {
    if (!item.goods_service_id) return "لطفاً کالا/خدمت را انتخاب کنید.";
    const payload = buildItemPayload(item);
    if (!Number.isFinite(payload.quantity) || payload.quantity <= 0)
      return "تعداد باید عددی بزرگ‌تر از صفر باشد.";
    if (!Number.isFinite(payload.length) || payload.length <= 0)
      return "طول باید عددی بزرگ‌تر از صفر باشد.";
    if (!Number.isFinite(payload.width) || payload.width <= 0)
      return "عرض باید عددی بزرگ‌تر از صفر باشد.";
    if (!Number.isFinite(payload.unit_price) || payload.unit_price < 0)
      return "قیمت واحد نمی‌تواند منفی باشد.";
    return null;
  };

  // POST /proforma-invoices/{invoice_id}/items
  const handleCreateItem = async (index: number) => {
    if (commissionLocked) {
      Swal.fire("امکان ویرایش نیست", "برای تغییر اقلام، ابتدا تأیید پورسانت را ادمین لغو کند.", "warning");
      return;
    }
    if (!editingId || itemSubmittingIndex !== null) return;
    const item = editValidation.values.items[index];
    const validationError = validateItemPayload(item);
    if (validationError) {
      Swal.fire("خطا", validationError, "error");
      return;
    }

    setItemSubmittingIndex(index);
    try {
      const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
      const res = await axios.post(
        `${BASE_URL}/proforma-invoices/${editingId}/items`,
        buildItemPayload(item),
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } }
      );
      if (res.status === 201 || res.status === 200) {
        await refreshAfterItemChange(editingId);
        Swal.fire("عملیات موفق", "قلم جدید ثبت شد", "success");
      }
    } catch (error: any) {
      console.error("Create item error:", error);
      if (error.response?.status === 401) {
        navigate("/login");
        return;
      }
      Swal.fire("خطا", itemApiErrorMessage(error, "خطا در ثبت قلم"), "error");
    } finally {
      setItemSubmittingIndex(null);
    }
  };

  // PATCH /proforma-invoices/{invoice_id}/items/{item_id}
  const handleUpdateItem = async (index: number) => {
    if (commissionLocked) {
      Swal.fire("امکان ویرایش نیست", "برای تغییر اقلام، ابتدا تأیید پورسانت را ادمین لغو کند.", "warning");
      return;
    }
    if (!editingId || itemSubmittingIndex !== null) return;
    const item = editValidation.values.items[index];
    if (!item.id) return;
    const validationError = validateItemPayload(item);
    if (validationError) {
      Swal.fire("خطا", validationError, "error");
      return;
    }

    setItemSubmittingIndex(index);
    try {
      const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
      const res = await axios.patch(
        `${BASE_URL}/proforma-invoices/${editingId}/items/${item.id}`,
        buildItemPayload(item),
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` } }
      );
      if (res.status === 200) {
        await refreshAfterItemChange(editingId);
        Swal.fire("عملیات موفق", "قلم با موفقیت ویرایش شد", "success");
      }
    } catch (error: any) {
      console.error("Update item error:", error);
      if (error.response?.status === 401) {
        navigate("/login");
        return;
      }
      Swal.fire("خطا", itemApiErrorMessage(error, "خطا در ویرایش قلم"), "error");
    } finally {
      setItemSubmittingIndex(null);
    }
  };

  // DELETE /proforma-invoices/{invoice_id}/items/{item_id}
  const handleDeleteItem = async (index: number) => {
    if (commissionLocked) {
      Swal.fire("امکان ویرایش نیست", "برای تغییر اقلام، ابتدا تأیید پورسانت را ادمین لغو کند.", "warning");
      return;
    }
    if (!editingId || itemSubmittingIndex !== null) return;
    const item = editValidation.values.items[index];

    // قلمی که هنوز روی سرور ثبت نشده، فقط از فهرست محلی حذف می‌شود.
    if (!item.id) {
      removeEditItem(index);
      return;
    }

    const confirmed = await Swal.fire({
      title: "حذف قلم",
      text: `آیا حذف «${getItemLabel(item, index)}» را تأیید می‌کنید؟`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "بلی",
      cancelButtonText: "خیر",
    });
    if (!confirmed.isConfirmed) return;

    setItemSubmittingIndex(index);
    try {
      const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
      const res = await axios.delete(
        `${BASE_URL}/proforma-invoices/${editingId}/items/${item.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.status === 200) {
        await refreshAfterItemChange(editingId);
        Swal.fire("عملیات موفق", "قلم حذف شد", "success");
      }
    } catch (error: any) {
      console.error("Delete item error:", error);
      if (error.response?.status === 401) {
        navigate("/login");
        return;
      }
      // با خطا هیچ وضعیت محلی تغییر نمی‌کند.
      Swal.fire("خطا", itemApiErrorMessage(error, "خطا در حذف قلم"), "error");
    } finally {
      setItemSubmittingIndex(null);
    }
  };

  const handleEdit = async (invoice: ProformaInvoice) => {
    setEditingId(invoice.id);
    const detail = (await fetchInvoiceDetail(invoice.id)) || invoice;
    setEditingInvoice(detail);
    const items: InvoiceItem[] = toEditItems(detail.items);

    editValidation.setValues({
      contact_id: String(detail.contact_id ?? ""),
      customer_id: String(detail.customer_id ?? ""),
      commission_percentage:
        detail.commission_percentage === null || detail.commission_percentage === undefined
          ? ""
          // مقدار صفر معتبر است و نباید با خالی یکی شود، پس رشته‌اش می‌کنیم.
          : String(Number(detail.commission_percentage)),
      invoice_date: detail.invoice_date || getPersianDate(0),
      valid_until: detail.valid_until || getPersianDate(1),
      sale_type: detail.sale_type || "domestic",
      currency_id: String(detail.currency_id ?? ""),
      items,
    });
    setShowEditModal(true);
  };

  const handleViewSpecs = async (invoice: ProformaInvoice) => {
    setSpecsLoading(true);
    setShowSpecsModal(invoice); // ابتدا یک نسخه از invoice فعلی نمایش داده شود
    try {
      const detail = await fetchInvoiceDetail(invoice.id);
      if (detail) {
        setShowSpecsModal(detail);
      } else {
        setShowSpecsModal(invoice);
      }
    } catch (error) {
      console.error("Error fetching detail:", error);
      setShowSpecsModal(invoice);
    } finally {
      setSpecsLoading(false);
    }
  };

  // ========== ثبت پرداخت (بدون تغییر) ==========
  const resetPaymentForm = () => ({
    payment_type_id: "",
    amount: "",
    due_date: getPersianDate(0),
    receipt_file: null as File | null,
    tracking_code: "",
    deposit_date: getPersianDate(0),
  });

  const handleRegisterPayment = async () => {
    if (!showRegisterPayment || paymentSubmitting) return;
    if (
      !paymentForm.payment_type_id ||
      !paymentForm.amount ||
      !paymentForm.due_date ||
      !paymentForm.receipt_file
    ) {
      Swal.fire("خطا", "تمام فیلدها اجباری است", "error");
      return;
    }
    if (isAdmin && (!paymentForm.tracking_code || !paymentForm.deposit_date)) {
      Swal.fire("خطا", "کد رهگیری و تاریخ واریز الزامی است", "error");
      return;
    }

    const formData = new FormData();
    formData.append("payment_type_id", paymentForm.payment_type_id);
    formData.append("amount", paymentForm.amount.replace(/,/g, ""));
    formData.append("due_date", paymentForm.due_date);
    formData.append("receipt_file", paymentForm.receipt_file);
    if (isAdmin) {
      formData.append("accounting_confirmed", "true");
      formData.append("tracking_code", paymentForm.tracking_code);
      formData.append("deposit_date", paymentForm.deposit_date);
    }

    setPaymentSubmitting(true);
    try {
      const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
      const res = await axios.post(
        `${BASE_URL}/proforma-invoices/${showRegisterPayment}/payments`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (res.status === 200 || res.status === 201) {
        Swal.fire("عملیات موفق", "پرداخت با موفقیت ثبت شد", "success");
        refreshAccountingMenuCounts();
        setReload(!reload);
        setShowRegisterPayment(null);
        setPaymentForm(resetPaymentForm());
        if (showPaymentsModal) {
          fetchPayments(showPaymentsModal);
        }
      }
    } catch (error: any) {
      console.error("Payment register error:", error);
      if (error.response?.status === 401) {
        navigate("/login");
        return;
      }
      // پیغام خطای سرور انگلیسی است؛ apiErrorMessage آن را به جمله‌ی فارسی
      // گویا تبدیل می‌کند (تکراری بودن کد ملی/شماره همراه، فیلد الزامی و ...).
      let msg = apiErrorMessage(error, "خطا در ثبت پرداخت");
      Swal.fire("خطا", msg, "error");
    } finally {
      setPaymentSubmitting(false);
    }
  };

  const handleDeletePayment = async (invoiceId: number, paymentId: number) => {
    try {
      const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
      const res = await axios.delete(`${BASE_URL}/proforma-invoices/${invoiceId}/payments/${paymentId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 200) {
        Swal.fire("عملیات موفق", "پرداخت با موفقیت حذف شد", "success");
        refreshAccountingMenuCounts();
        fetchPayments(invoiceId);
        setReload(!reload);
      }
    } catch (error: any) {
      console.error("Delete payment error:", error);
      if (error.response?.status === 401) {
        navigate("/login");
        return;
      }
      if (error.response?.status === 409) {
        Swal.fire("خطا", apiErrorMessage(error, "به علت تایید حسابداری امکان ویرایش وجود ندارد"), "error");
      } else {
        Swal.fire("خطا", apiErrorMessage(error, "خطا در حذف پرداخت"), "error");
      }
    }
  };

  const openEditPayment = (payment: Payment) => {
    setEditPaymentTarget(payment);
    setEditPaymentForm({
      payment_type_id: String(payment.payment_type_id),
      amount: formatPrice(payment.amount),
      due_date: payment.due_date || getPersianDate(0),
      receipt_file: null,
      accounting_confirmed: payment.accounting_confirmed,
      tracking_code: payment.tracking_code || "",
      deposit_date: payment.deposit_date || getPersianDate(0),
    });
  };

  const handleUpdatePayment = async () => {
    if (!showPaymentsModal || !editPaymentTarget || editPaymentSubmitting) return;
    if (!editPaymentForm.payment_type_id || !editPaymentForm.amount || !editPaymentForm.due_date) {
      Swal.fire("خطا", "تمام فیلدها اجباری است", "error");
      return;
    }
    if (isAdmin && editPaymentForm.accounting_confirmed && (!editPaymentForm.tracking_code || !editPaymentForm.deposit_date)) {
      Swal.fire("خطا", "کد رهگیری و تاریخ واریز الزامی است", "error");
      return;
    }

    const formData = new FormData();
    formData.append("payment_type_id", editPaymentForm.payment_type_id);
    formData.append("amount", editPaymentForm.amount.replace(/,/g, ""));
    formData.append("due_date", editPaymentForm.due_date);
    if (editPaymentForm.receipt_file) {
      formData.append("receipt_file", editPaymentForm.receipt_file);
    }
    if (isAdmin) {
      formData.append("accounting_confirmed", editPaymentForm.accounting_confirmed ? "true" : "false");
      if (editPaymentForm.accounting_confirmed) {
        formData.append("tracking_code", editPaymentForm.tracking_code);
        formData.append("deposit_date", editPaymentForm.deposit_date);
      }
    }

    setEditPaymentSubmitting(true);
    try {
      const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
      const res = await axios.patch(
        `${BASE_URL}/proforma-invoices/${showPaymentsModal}/payments/${editPaymentTarget.id}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.status === 200) {
        Swal.fire("عملیات موفق", "پرداخت با موفقیت ویرایش شد", "success");
        refreshAccountingMenuCounts();
        setEditPaymentTarget(null);
        fetchPayments(showPaymentsModal);
        setReload(!reload);
      }
    } catch (error: any) {
      console.error("Update payment error:", error);
      if (error.response?.status === 401) {
        navigate("/login");
        return;
      }
      if (error.response?.status === 409) {
        Swal.fire("خطا", apiErrorMessage(error, "به علت تایید حسابداری امکان ویرایش وجود ندارد"), "error");
        return;
      }
      // پیغام خطای سرور انگلیسی است؛ apiErrorMessage آن را به جمله‌ی فارسی
      // گویا تبدیل می‌کند (تکراری بودن کد ملی/شماره همراه، فیلد الزامی و ...).
      let msg = apiErrorMessage(error, "خطا در ویرایش پرداخت");
      Swal.fire("خطا", msg, "error");
    } finally {
      setEditPaymentSubmitting(false);
    }
  };

  const startApproval = (invoiceId: number, paymentId: number) => {
    setApprovalTarget({ invoiceId, paymentId });
    setApprovalStep("confirm");
    setApprovalForm({ tracking_code: "", deposit_date: getPersianDate(0) });
  };

  const confirmApprovalStart = () => setApprovalStep("form");
  const cancelApproval = () => setApprovalTarget(null);

  const submitApproval = async () => {
    if (!approvalTarget) return;
    if (!approvalForm.tracking_code || !approvalForm.deposit_date) {
      Swal.fire("خطا", "کد رهگیری و تاریخ واریز الزامی است", "error");
      return;
    }

    try {
      const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
      const res = await axios.post(
        `${BASE_URL}/proforma-invoices/${approvalTarget.invoiceId}/payments/${approvalTarget.paymentId}/confirm-accounting`,
        {
          tracking_code: approvalForm.tracking_code,
          deposit_date: approvalForm.deposit_date,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (res.status === 200) {
        Swal.fire("عملیات موفق", "پرداخت با موفقیت تأیید شد", "success");
        refreshAccountingMenuCounts();
        setApprovalTarget(null);
        fetchPayments(approvalTarget.invoiceId);
        setReload(!reload);
      }
    } catch (error: any) {
      console.error("Approval error:", error);
      if (error.response?.status === 401) {
        navigate("/login");
        return;
      }
      Swal.fire("خطا", apiErrorMessage(error, "خطا در تأیید پرداخت"), "error");
    }
  };

  const handleSalesManagerApprove = async (invoiceId: number) => {
    try {
      const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
      const res = await axios.post(
        `${BASE_URL}/proforma-invoices/${invoiceId}/approve-sales-manager`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (res.status === 200) {
        Swal.fire("عملیات موفق", "تأیید مدیر فروش با موفقیت ثبت شد", "success");
        setReload(!reload);
      }
    } catch (error: any) {
      console.error("Sales manager approve error:", error);
      if (error.response?.status === 401) {
        navigate("/login");
        return;
      }
      Swal.fire("خطا", apiErrorMessage(error, "خطا در تأیید مدیر فروش"), "error");
    }
  };

  const handleUnapproveSalesManager = async (invoiceId: number) => {
    try {
      const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
      const res = await axios.post(
        `${BASE_URL}/proforma-invoices/${invoiceId}/unapprove-sales-manager`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (res.status === 200) {
        // پاسخ همین مسیر وضعیت جدید سفارش را برمی‌گرداند؛ هیچ محاسبه‌ای در فرانت‌اند
        // لازم نیست و فقط فهرست دوباره از سرور خوانده می‌شود.
        Swal.fire("عملیات موفق", "برگشت تأیید مدیر فروش با موفقیت انجام شد", "success");
        setReload(!reload);
      }
    } catch (error: any) {
      console.error("Unapprove error:", error);
      const status = error.response?.status;
      if (status === 401) {
        navigate("/login");
        return;
      }
      // ۴۰۳: نقش کاربر اجازه ندارد (مثلاً مدیر فروش).
      // ۴۰۹: تولید شروع شده و سرور برای حفظ سابقه‌ی تأیید، بازگشت را رد می‌کند.
      if (status === 409) {
        Swal.fire(
          "امکان بازگشت نیست",
          apiErrorMessage(
            error,
            "تولید این سفارش شروع شده است و برای حفظ سابقه‌ی تأیید، امکان بازگشت وجود ندارد."
          ),
          "error"
        );
        return;
      }
      Swal.fire("خطا", apiErrorMessage(error, "خطا در برگشت تأیید"), "error");
    }
  };

  // ========== تأیید و برداشتن تأیید پورسانت ==========
  // این جریان کاملاً از تأیید سفارش (approve-sales-manager) جداست و هیچ‌کدام از
  // مبلغ، مانده یا وضعیت سفارش را عوض نمی‌کند. زمان تأیید فقط از پاسخ سرور خوانده
  // می‌شود، نه از ساعت مرورگر.
  const [commissionBusyId, setCommissionBusyId] = useState<number | null>(null);

  const runCommissionAction = async (
    invoiceId: number,
    action: "approve-commission" | "unapprove-commission",
    successText: string
  ) => {
    if (commissionBusyId !== null) return;
    setCommissionBusyId(invoiceId);
    try {
      const token =
        localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
      // این دو درخواست بدنه ندارند و پاسخ‌شان جزئیات به‌روز پیش‌فاکتور است.
      const res = await axios.post(
        `${BASE_URL}/proforma-invoices/${invoiceId}/${action}`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (res.status >= 200 && res.status < 300) {
        Swal.fire("عملیات موفق", successText, "success");
        // وضعیت از پاسخ سرور گرفته می‌شود تا فرم با آنچه ثبت شده هماهنگ بماند.
        setShowSpecsModal((prev) => (prev && prev.id === invoiceId ? res.data : prev));
        setReload((prev) => !prev);
      }
    } catch (error: any) {
      console.error("Commission action error:", error);
      const status = error.response?.status;
      if (status === 401) {
        navigate("/login");
        return;
      }
      // ۴۰۹: درخواست تکراری، نبود رابط/درصد، یا تلاش برای تغییر مقدار تأییدشده.
      // در این حالت طبق مستند باید وضعیت سفارش دوباره خوانده شود.
      if (status === 409) setReload((prev) => !prev);
      Swal.fire("خطا", apiErrorMessage(error, "انجام این عملیات ممکن نشد"), "error");
    } finally {
      setCommissionBusyId(null);
    }
  };

  const handleApproveCommission = (invoice: ProformaInvoice) => {
    const invoiceId = invoice.id;
    return fetchInvoiceDetail(invoiceId).then(async (detail) => {
      if (!detail) return;
      const amount = detail.commission_amount_display
        ? `${detail.commission_amount_display} ریال`
        : formatCommissionAmount(
            detail.commission_amount === null || detail.commission_amount === undefined
              ? null
              : Number(detail.commission_amount)
          );
      const result = await Swal.fire({
        title: "تأیید پورسانت",
        text: `آیا درصد پورسانت ${detail.commission_percentage ?? "-"}٪ با مبلغ ${amount} را برای رابط ${detail.contact_name || "-"} تأیید می‌کنید؟`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "بله، تأیید کن",
        cancelButtonText: "خیر",
      });
      if (!result.isConfirmed) return;
      await runCommissionAction(invoiceId, "approve-commission", "تأیید پورسانت ثبت شد");
      // رکوردِ در حال ویرایش هم تازه می‌شود تا تیک و زمان بلافاصله درست دیده شوند.
      if (editingId === invoiceId) refreshEditingInvoice(invoiceId);
    });
  };

  const handleUnapproveCommission = (invoiceId: number) =>
    runCommissionAction(invoiceId, "unapprove-commission", "تأیید پورسانت برداشته شد").then(
      () => {
        if (editingId === invoiceId) refreshEditingInvoice(invoiceId);
      }
    );

  /** جزئیات پیش‌فاکتورِ در حال ویرایش را دوباره از سرور می‌خواند. */
  const refreshEditingInvoice = async (invoiceId: number) => {
    const detail = await fetchInvoiceDetail(invoiceId);
    if (detail) setEditingInvoice(detail);
  };

  /**
   * «جمع کل بدون مالیات بر ارزش افزوده» برای فرم ایجاد.
   * سفارش هنوز ثبت نشده و سرور gross_amount نداده، پس از روی همان اقلامِ فرم
   * حساب می‌شود: مجموع (تعداد × طول × عرض × قیمت واحد).
   */
  const createGrossAmount = useMemo(
    () =>
      createValidation.values.items.reduce((sum, item) => {
        const quantity = stripCommas(item.quantity);
        const length = stripCommas(item.length);
        const width = stripCommas(item.width);
        const unitPrice = stripCommas(item.unit_price);
        const line = quantity * length * width * unitPrice;
        return Number.isFinite(line) ? sum + line : sum;
      }, 0),
    [createValidation.values.items]
  );

  // ===== وضعیت تأیید پورسانت =====
  // تأیید برای مدیر فروش (۴)، ادمین (۱) و سوپرادمین (۰) مجاز است. خودِ دکمه در ستون
  // «تأیید پورسانت» جدول است، چون سرور به مدیر فروش اجازه‌ی ویرایش پیش‌فاکتور
  // نمی‌دهد و اگر کنترل داخل فرم ویرایش بود هرگز به آن نمی‌رسید.
  const mayApproveCommission = isAdmin || canApproveSalesManager(currentUserRoleId);
  // در فرم ویرایش فقط برای قفل‌کردن رابط و درصدِ تأییدشده استفاده می‌شود.
  const commissionLocked = Boolean(editingInvoice?.commission_approved);

  const handleCeoApprove = async (invoiceId: number) => {
    try {
      const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
      const res = await axios.post(
        `${BASE_URL}/proforma-invoices/${invoiceId}/approve-ceo`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (res.status === 200) {
        Swal.fire("عملیات موفق", "تأیید مدیرعامل با موفقیت ثبت شد", "success");
        setReload(!reload);
      }
    } catch (error: any) {
      console.error("CEO approve error:", error);
      if (error.response?.status === 401) {
        navigate("/login");
        return;
      }
      if (error.response?.status === 404) {
        Swal.fire("در دسترس نیست", "سرویس تأیید مدیرعامل هنوز روی سرور پیاده‌سازی نشده است.", "warning");
        return;
      }
      Swal.fire("خطا", apiErrorMessage(error, "خطا در تأیید مدیرعامل"), "error");
    }
  };

  const handleUnapproveCeo = async (invoiceId: number) => {
    try {
      const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
      const res = await axios.post(
        `${BASE_URL}/proforma-invoices/${invoiceId}/unapprove-ceo`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (res.status === 200) {
        Swal.fire("عملیات موفق", "برگشت تأیید مدیرعامل با موفقیت انجام شد", "success");
        setReload(!reload);
      }
    } catch (error: any) {
      console.error("Unapprove CEO error:", error);
      if (error.response?.status === 401) {
        navigate("/login");
        return;
      }
      if (error.response?.status === 404) {
        Swal.fire("در دسترس نیست", "سرویس تأیید مدیرعامل هنوز روی سرور پیاده‌سازی نشده است.", "warning");
        return;
      }
      Swal.fire("خطا", apiErrorMessage(error, "خطا در برگشت تأیید مدیرعامل"), "error");
    }
  };

  const handleViewPayments = (invoiceId: number) => {
    setShowPaymentsModal(invoiceId);
    fetchPayments(invoiceId);
  };

  // ========== Searchable Options ==========
  const customerSearch = useSearchableOptions(
    customers,
    (c) => c.first_name + (c.last_name ? ` ${c.last_name}` : ""),
    (c) => String(c.id)
  );
  const contactSearch = useSearchableOptions(
    contacts,
    (c) => c.full_name || `${c.first_name} ${c.last_name}`.trim(),
    (c) => String(c.id)
  );
  /** برچسب یک رابط از روی شناسه‌اش. */
  const contactLabel = (id: string): string => {
    const found = contacts.find((c) => String(c.id) === id);
    return found ? found.full_name || `${found.first_name} ${found.last_name}`.trim() : "";
  };
  const currencySearch = useSearchableOptions(currencies, (c) => c.name, (c) => String(c.id));
  const paymentTypeSearch = useSearchableOptions(paymentTypes, (p) => p.name, (p) => String(p.id));

  const [goodsQuery, setGoodsQuery] = useState("");
  const [goodsSearchResults, setGoodsSearchResults] = useState<GoodsService[] | null>(null);
  const [goodsRecordCache, setGoodsRecordCache] = useState<Record<string, GoodsService>>({});
  useEffect(() => {
    const list = goodsSearchResults ?? goods;
    if (!list.length) return;
    setGoodsRecordCache((prev) => {
      const next = { ...prev };
      list.forEach((g) => {
        next[String(g.id)] = g;
      });
      return next;
    });
  }, [goods, goodsSearchResults]);

  const getGoodsRecord = (id: string | number | undefined): GoodsService | undefined => {
    if (!id) return undefined;
    return goodsRecordCache[String(id)];
  };

  /**
   * برچسب یک کالا: کد، عنوان و سپس همه‌ی مشخصه‌های پانل که از خودِ کد
   * رمزگشایی می‌شوند (نوع، ضخامت، ورق رو و پشت، دانسیته، نوع فوم و رنگ‌ها).
   */
  const buildGoodsLabel = (g?: GoodsService | null): string => {
    if (!g) return "";
    if (g.display_label) return g.display_label;
    const description = g.goods_full_description || goodsCodeDescription(g.code);
    return description ? `${g.code} - ${description}` : `${g.code} - ${g.title}`;
  };

  const getGoodsLabel = (id: string | number | undefined): string =>
    buildGoodsLabel(getGoodsRecord(id));

  useEffect(() => {
    if (!goodsQuery) {
      setGoodsSearchResults(null);
      return;
    }
    const timeout = setTimeout(async () => {
      try {
        const token = localStorage.getItem("app_at_mahan") || sessionStorage.getItem("app_at_mahan");
        const res = await axios.get(`${BASE_URL}/goods-services/`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { search: toEnglishDigits(goodsQuery.trim()), size: 50 },
        });
        setGoodsSearchResults(extractList(res.data));
      } catch (error) {
        console.error("Error searching goods/services:", error);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [goodsQuery]);

  const goodsOptions = useMemo(() => {
    const list = goodsSearchResults ?? goods;
    return list.map((g) => ({ value: String(g.id), label: buildGoodsLabel(g) }));
  }, [goodsSearchResults, goods]);


  const saleTypeOptions = [
    { value: "domestic", label: "داخلی با بسته‌بندی" },
    { value: "domestic_without_packaging", label: "داخلی بدون بسته‌بندی" },
  ];

  // ========== ستون‌های جدول ==========
  const columns = useMemo(
    () => {
      const rows = getProformaInvoicesColumns({
        isAdmin,
        isSalesExpert,
        isSalesManager,
        getSalesExpertName,
        handleViewSpecs,
        handleViewPayments,
        handleEdit,
        handleSalesManagerApprove,
        handleUnapproveSalesManager,
        handleCeoApprove,
        handleUnapproveCeo,
        setPaymentForm,
        resetPaymentForm,
        setShowRegisterPayment,
        setDeleteTargetId,
        handlePrint,
        showDateColumn,
        readOnly,
        mayRegisterPayment,
        mayApproveCommission,
        commissionBusyId,
        handleApproveCommission,
        handleUnapproveCommission,
      });
      if (paymentConfirmation) {
        rows.unshift({
          header: "وضعیت تأیید واریزی",
          accessorKey: "has_pending_payments",
          meta: { serverSortBy: "has_pending_payments" },
          exportValue: (row: ProformaInvoice) => row.has_pending_payments ? "در انتظار تأیید" : "تأیید شده / بدون واریزی معلق",
          cell: (cell: any) => cell.getValue()
            ? <span className="text-amber-600">در انتظار تأیید</span>
            : <span className="text-green-600">تأیید شده / بدون واریزی معلق</span>,
        });
      }
      return rows;
    },
    [
      isAdmin,
      isSalesExpert,
      isSalesManager,
      salesExpertNames,
      handlePrint,
      showDateColumn,
      readOnly,
      paymentConfirmation,
      mayRegisterPayment,
      mayApproveCommission,
      commissionBusyId,
      // eslint-disable-next-line react-hooks/exhaustive-deps
    ]
  );

  // ========== رندر ==========
  return (
    <React.Fragment>
      <BreadCrumb pageTitle={pageTitle} title={pageTitle} />
      {errorMessage && <div className="text-center text-red-500 mb-3">{errorMessage}</div>}

      <div className="card">
        <div className="card-body">
          <div className="flex items-center gap-3 mb-4">
            <h6 className="text-15 grow">
              {totalLabel} (<b className="total-Employs">{totalItems}</b>)
            </h6>
            {showFilters && (
              <div className="shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    // پیش‌نویس با فیلترهای فعلی هم‌گام می‌شود تا تغییرات لغوشده نماند.
                    setDraftFilters(filters);
                    setShowFilterModal(true);
                  }}
                  className="btn bg-white border-slate-300 text-slate-600 hover:bg-slate-100 dark:bg-zink-700 dark:border-zink-500 dark:text-zink-100"
                >
                  <SlidersHorizontal className="inline-block size-4 ltr:mr-1 rtl:ml-1 align-middle" />
                  <span className="align-middle">فیلترها</span>
                  {activeFilterCount > 0 && (
                    <span className="inline-flex items-center justify-center w-5 h-5 ltr:ml-1.5 rtl:mr-1.5 text-[10px] font-bold text-white align-middle rounded-full bg-custom-500">
                      {activeFilterCount}
                    </span>
                  )}
                </button>
              </div>
            )}
            {salesCreationBlocked && (
              <div className="text-sm font-medium text-red-600" role="status">
                فروش تا اطلاع ثانوی غیرفعال است. برای کسب اطلاعات بیشتر با مدیر فروش تماس بگیرید.
              </div>
            )}
            {mayCreateProformaNow && (
              <div className="shrink-0">
                <button
                  onClick={() => {
                    createValidation.resetForm();
                    setShowCreateModal(true);
                  }}
                  type="button"
                  className="text-white btn bg-custom-500 border-custom-500 hover:text-white hover:bg-custom-600"
                >
                  <span className="align-middle rtl">ایجاد پیش‌فاکتور</span>{" "}
                  <Plus className="inline-block size-4 rtl" />
                </button>
              </div>
            )}
          </div>

          {loading ? (
            <div className="flex justify-center items-center h-32">
              <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full border-custom-500 border-t-transparent"></div>
            </div>
          ) : invoices.length > 0 ? (
            <TableContainer
              isPagination={true}
              columns={columns}
              data={invoices}
              serverSorting={{ sortBy, sortOrder, onChange: changeServerSort }}
              customPageSize={pageSize}
              // فقط ۸ ستون اول نمایش داده می‌شود؛ بقیه با دکمه‌ی + هر ردیف باز
              // می‌شوند تا جدول عریض، صفحه را به اسکرول افقی نیندازد.
              customnumberfortable={tableColumns}
              customnumberformobile={mobileColumns}
              exportAllRows={exportRows}
              serverPagination={{
                currentPage: currentPage,
                totalPages: totalPages,
                onPageChange: setCurrentPage,
                pageSize,
                onPageSizeChange: (size) => {
                  setPageSize(size);
                  setCurrentPage(1);
                },
              }}
              divclassName="-mx-5 overflow-x-auto"
              tableclassName="w-full whitespace-nowrap"
              theadclassName="ltr:text-left rtl:text-right bg-[#e2e8f0] text-[#334155] dark:bg-[#334155] dark:text-[#f1f5f9]"
              thclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 font-semibold border-b border-slate-300 dark:border-slate-600"
              tdclassName="px-3.5 py-2.5 first:pl-5 last:pr-5 border-y border-slate-200 dark:border-zink-400/80"
              PaginationClassName="flex flex-col items-center gap-4 px-4 mt-4 md:flex-row"
            />
          ) : (
            <LoadingCounter count={1} text={emptyText} />
          )}
        </div>
      </div>

      {/* ========== مودال فیلترها (همه سفارش‌ها و سایر سفارش‌ها) ========== */}
      {/* ترتیب فیلدها دقیقاً مطابق مستند: نام مشتری، بازه‌ی تاریخ، شماره فاکتور،
          شرح کالا، وضعیت سفارش. */}
      <Modal
        show={showFilterModal}
        onHide={() => setShowFilterModal(false)}
        modal-center="true"
        dialogClassName="w-screen md:w-[40rem]"
      >
        <Modal.Header
          className="flex items-center justify-between p-4 border-b dark:border-zink-500"
          closeButtonClass="transition-all duration-200 ease-linear text-slate-500 hover:text-red-500 dark:text-zink-200 dark:hover:text-red-500"
        >
          <Modal.Title className="text-16">فیلترها</Modal.Title>
        </Modal.Header>
        <Modal.Body className="max-h-[calc(100vh-220px)] p-4 overflow-y-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" dir="rtl">
            <div>
              <label className="block text-sm font-medium mb-1">نام مشتری</label>
              <input
                type="text"
                className="form-input w-full"
                placeholder="بخشی از نام مشتری"
                value={draftFilters.customerName}
                onChange={(e) =>
                  setDraftFilters((prev) => ({ ...prev, customerName: e.target.value }))
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">شماره فاکتور</label>
              <input
                type="text"
                className="form-input w-full"
                placeholder="مثلاً ۱۰۱"
                value={draftFilters.invoiceNumber}
                onChange={(e) =>
                  setDraftFilters((prev) => ({ ...prev, invoiceNumber: e.target.value }))
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">از تاریخ</label>
              <DatePicker
                calendar={persian}
                locale={persian_fa}
                format="YYYY/MM/DD"
                value={draftFilters.dateFrom || ""}
                onChange={(date: any) =>
                  setDraftFilters((prev) => ({
                    ...prev,
                    dateFrom: date ? normalizeDigits(date.format("YYYY/MM/DD")) : "",
                  }))
                }
                inputClass="form-input w-full"
                containerClassName="w-full"
                placeholder="انتخاب تاریخ"
                editable={false}
                calendarPosition="bottom-center"
                // بدون portal، تقویم زیر مودال (z-index 1050) می‌افتد و با
                // overflow-y-auto بدنه‌ی مودال هم بریده می‌شود.
                portal
                portalTarget={typeof document !== "undefined" ? document.body : undefined}
                zIndex={9999}
                mobileLabels={{ OK: "تایید", CANCEL: "انصراف" }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">تا تاریخ</label>
              <DatePicker
                calendar={persian}
                locale={persian_fa}
                format="YYYY/MM/DD"
                value={draftFilters.dateTo || ""}
                onChange={(date: any) =>
                  setDraftFilters((prev) => ({
                    ...prev,
                    dateTo: date ? normalizeDigits(date.format("YYYY/MM/DD")) : "",
                  }))
                }
                inputClass="form-input w-full"
                containerClassName="w-full"
                placeholder="انتخاب تاریخ"
                editable={false}
                calendarPosition="bottom-center"
                // بدون portal، تقویم زیر مودال (z-index 1050) می‌افتد و با
                // overflow-y-auto بدنه‌ی مودال هم بریده می‌شود.
                portal
                portalTarget={typeof document !== "undefined" ? document.body : undefined}
                zIndex={9999}
                mobileLabels={{ OK: "تایید", CANCEL: "انصراف" }}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">شرح کالا</label>
              <input
                type="text"
                className="form-input w-full"
                placeholder="کد، عنوان یا شرح کالا"
                value={draftFilters.goodsDescription}
                onChange={(e) =>
                  setDraftFilters((prev) => ({ ...prev, goodsDescription: e.target.value }))
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">وضعیت سفارش</label>
              <Select
                options={orderStatusFilterOptions}
                value={
                  draftFilters.orderStatus
                    ? orderStatusFilterOptions.find((o) => o.value === draftFilters.orderStatus)
                    : null
                }
                onChange={(opt: any) =>
                  setDraftFilters((prev) => ({ ...prev, orderStatus: opt?.value || "" }))
                }
                isClearable
                placeholder="همه وضعیت‌ها"
                styles={selectStyles}
                menuPortalTarget={typeof document !== "undefined" ? document.body : undefined}
                menuPosition="fixed"
                classNamePrefix="react-select"
              />
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-end gap-2 mt-5">
            <button
              type="button"
              onClick={() => {
                setDraftFilters(EMPTY_FILTERS);
                setFilters(EMPTY_FILTERS);
              }}
              className="btn bg-white border-slate-300 text-slate-600 hover:bg-slate-100 dark:bg-zink-700 dark:border-zink-500 dark:text-zink-100"
            >
              <X className="inline-block size-4 ltr:mr-1 rtl:ml-1 align-middle" />
              <span className="align-middle">پاک کردن فیلترها</span>
            </button>
            <button
              type="button"
              onClick={() => {
                // فیلترها یک‌جا اعمال می‌شوند تا با هر ضربه‌ی کیبورد درخواست نرود.
                setFilters(draftFilters);
                setShowFilterModal(false);
              }}
              className="text-white btn bg-custom-500 border-custom-500 hover:bg-custom-600"
            >
              اعمال فیلترها
            </button>
          </div>
        </Modal.Body>
      </Modal>
      {/* ========== مودال ایجاد ========== */}
      {/* ========== مودال ایجاد ========== */}
      <Modal
        show={showCreateModal}
        onHide={() => setShowCreateModal(false)}
        modal-center="true"
        dialogClassName="w-screen md:w-[68rem]"
      >
        <Modal.Header
          className="flex items-center justify-between p-3.5 border-b border-slate-200 dark:border-zink-500 bg-gradient-to-r from-slate-50 to-white dark:from-zink-800 dark:to-zink-700 rounded-t-lg"
          closeButtonClass="transition-all duration-200 ease-linear text-slate-500 hover:text-red-500 dark:text-white dark:hover:text-red-400"
        >
          <Modal.Title className="text-lg font-bold text-slate-800 dark:text-zink-100 flex items-center gap-2">
            <span className="inline-block w-1 h-6 bg-custom-500 rounded-full"></span>
            ایجاد پیش‌فاکتور جدید
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="max-h-[calc(100vh-160px)] p-4 overflow-y-auto bg-slate-100 dark:bg-zink-700/40">
          <form onSubmit={createValidation.handleSubmit} className="space-y-4">
            {/* ===== بخش اطلاعات اصلی ===== */}
            <div className="bg-white dark:bg-zink-600/30 rounded-xl shadow-sm p-4">
              <h6 className="text-sm font-semibold text-slate-700 dark:text-zink-100 mb-3 flex items-center gap-2">
                <span className="inline-block w-1 h-4 bg-custom-500 rounded-full"></span>
                اطلاعات پایه
              </h6>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* ترتیب مستند نسخه ۵: اول «مشتری»، بعد «رابط»، بعد «درصد پورسانت». */}

                <div>
                  <label className="block text-xs font-medium text-slate-700 dark:text-zink-200 mb-1">
                    مشتری
                  </label>
                  <Select
                    options={customerSearch.options}
                    onInputChange={customerSearch.onInputChange}
                    value={
                      createValidation.values.customer_id
                        ? {
                          value: createValidation.values.customer_id,
                          label: getCustomerFullName(
                            customers.find((c) => String(c.id) === createValidation.values.customer_id)
                          ),
                        }
                        : null
                    }
                    onChange={(opt: any) =>
                      createValidation.setFieldValue("customer_id", opt?.value || "")
                    }
                    placeholder="جست‌وجوی مشتری..."
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    isClearable
                    noOptionsMessage={() => "موردی یافت نشد"}
                  />
                  {createValidation.touched.customer_id &&
                    createValidation.errors.customer_id && (
                      <p className="text-red-500 text-xs mt-0.5 flex items-center gap-1">
                        <span className="inline-block w-1 h-1 bg-red-500 rounded-full"></span>
                        {typeof createValidation.errors.customer_id === "string"
                          ? createValidation.errors.customer_id
                          : ""}
                      </p>
                    )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 dark:text-zink-200 mb-1">
                    رابط
                  </label>
                  <Select
                    options={contactSearch.options}
                    onInputChange={contactSearch.onInputChange}
                    value={
                      createValidation.values.contact_id
                        ? {
                          value: createValidation.values.contact_id,
                          label: contactLabel(createValidation.values.contact_id),
                        }
                        : null
                    }
                    onChange={(opt: any) => {
                      const next = opt?.value || "";
                      createValidation.setFieldValue("contact_id", next);
                      // با پاک‌شدن رابط، درصد هم پاک و غیرفعال می‌شود.
                      if (!next) createValidation.setFieldValue("commission_percentage", "");
                    }}
                    placeholder="جست‌وجوی رابط..."
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    isClearable
                    noOptionsMessage={() => "رابطی ثبت نشده است"}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-700 dark:text-zink-200 mb-1">
                    درصد پورسانت
                  </label>
                  {/* فقط با انتخاب رابط فعال می‌شود؛ ورودش اجباری نیست و صفر معتبر است. */}
                  <input
                    type="text"
                    inputMode="decimal"
                    className="form-input w-full disabled:bg-slate-100 dark:disabled:bg-zink-500"
                    disabled={!createValidation.values.contact_id}
                    placeholder={
                      createValidation.values.contact_id
                        ? "بین ۰ تا ۱۰۰"
                        : "ابتدا رابط را انتخاب کنید"
                    }
                    value={createValidation.values.commission_percentage}
                    onChange={(e) =>
                      createValidation.setFieldValue(
                        "commission_percentage",
                        normalizeDigits(e.target.value)
                      )
                    }
                    onBlur={createValidation.handleBlur}
                    name="commission_percentage"
                  />
                  {createValidation.touched.commission_percentage &&
                    createValidation.errors.commission_percentage && (
                      <p className="text-red-500 text-xs mt-0.5">
                        {String(createValidation.errors.commission_percentage)}
                      </p>
                    )}
                  {/* تیک «تأیید مدیر فروش» فقط روی پیش‌فاکتور ذخیره‌شده معنا دارد. */}
                  <p className="mt-1.5 text-[11px] text-slate-500 dark:text-zink-300">
                    «تأیید مدیر فروش» برای پورسانت، پس از ثبت پیش‌فاکتور و از ستون
                    «مدیر فروش» جدول انجام می‌شود.
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-700 dark:text-zink-200 mb-1">
                    مبلغ پورسانت
                  </label>
                  {/* محاسبه‌شده است و دست‌نویس نمی‌شود: جمع کل بدون مالیات × درصد ÷ ۱۰۰ */}
                  <input
                    type="text"
                    readOnly
                    className="w-full cursor-default form-input bg-slate-100 dark:bg-zink-500"
                    value={formatCommissionAmount(
                      createValidation.values.contact_id
                        ? commissionAmount(
                          createGrossAmount,
                          createValidation.values.commission_percentage
                        )
                        : null
                    )}
                  />
                  <p className="mt-1 text-[11px] text-slate-500 dark:text-zink-300">
                    بر پایه‌ی جمع کل بدون مالیات بر ارزش افزوده
                  </p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-700 dark:text-zink-200 mb-1">
                    ارز <span className="text-red-500">*</span>
                  </label>
                  <Select
                    options={currencySearch.options}
                    onInputChange={currencySearch.onInputChange}
                    value={
                      createValidation.values.currency_id
                        ? {
                          value: createValidation.values.currency_id,
                          label:
                            currencies.find(
                              (c) => String(c.id) === createValidation.values.currency_id
                            )?.name || "",
                        }
                        : null
                    }
                    onChange={(opt: any) =>
                      createValidation.setFieldValue("currency_id", opt?.value || "")
                    }
                    placeholder="جست‌وجوی ارز..."
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                    isClearable
                    noOptionsMessage={() => "موردی یافت نشد"}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-700 dark:text-zink-200 mb-1">
                    نوع فروش <span className="text-red-500">*</span>
                  </label>
                  <Select
                    options={saleTypeOptions}
                    value={saleTypeOptions.find(
                      (s) => s.value === createValidation.values.sale_type
                    )}
                    onChange={(opt: any) =>
                      createValidation.setFieldValue("sale_type", opt?.value || "domestic")
                    }
                    styles={selectStyles}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />
                </div>

                <ModalData
                  data={createValidation.values.invoice_date}
                  label="تاریخ"
                  setData={(v) => createValidation.setFieldValue("invoice_date", v)}
                />

                <ModalData
                  label="تاریخ اعتبار"
                  data={createValidation.values.valid_until}
                  setData={(v) => createValidation.setFieldValue("valid_until", v)}
                />
              </div>
            </div>

            {/* ===== بخش کالا/خدمت‌ها ===== */}
            <div className="bg-white dark:bg-zink-600/30 rounded-xl shadow-sm p-4">
              <div className="flex items-center justify-between mb-3">
                <h6 className="text-sm font-semibold text-slate-700 dark:text-zink-100 flex items-center gap-2">
                  <span className="inline-block w-1 h-4 bg-custom-500 rounded-full"></span>
                  کالا/خدمت‌ها
                </h6>
                <button
                  type="button"
                  onClick={addCreateItem}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-custom-600 dark:text-custom-400 border border-custom-200 dark:border-custom-700 rounded-lg hover:bg-custom-50 dark:hover:bg-custom-900/20 transition-colors duration-200"
                >
                  <Plus className="size-3.5" />
                  افزودن کالا/خدمت
                </button>
              </div>

              {createValidation.values.items.map((item, index) => {
                const goodsLabel = getGoodsLabel(item.goods_service_id);
                return (
                  <div
                    key={index}
                    className="relative bg-white dark:bg-zink-600/30 rounded-lg p-3 mb-2.5 shadow-sm transition-shadow hover:shadow-md border border-slate-200 dark:border-zink-500"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <span className="text-xs font-semibold text-slate-700 dark:text-zink-200">
                        {goodsLabel || `کالا/خدمت ${index + 1}`}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeCreateItem(index)}
                        className="text-slate-400 hover:text-red-500 transition-colors p-0.5 rounded-full hover:bg-red-50 dark:hover:bg-red-900/20"
                      >
                        <Trash2 className="size-3.5" />
                      </button>
                    </div>

                    {/* «نک‌دار رو» و «نک‌دار زیر» بالای فیلدهای قلم قرار می‌گیرند تا ستون‌های
                        شبکه را اشغال نکنند. هر قلم چک‌باکس‌های مستقل خودش را دارد و پیش‌فرض
                        هر دو تیک‌خورده است. */}
                    <div className="flex flex-wrap gap-2 mb-2.5">
                      <label
                        htmlFor={`create-notch-top-${index}`}
                        className="inline-flex items-center gap-2 px-2.5 py-1.5 transition-colors border rounded-lg cursor-pointer select-none border-slate-300 dark:border-zink-500 hover:border-custom-400"
                      >
                        <input
                          type="checkbox"
                          id={`create-notch-top-${index}`}
                          className="w-4 h-4 rounded cursor-pointer text-custom-500 border-slate-300 dark:border-zink-500 focus:ring-custom-500"
                          checked={item.has_notch_top ?? true}
                          onChange={(e) =>
                            updateCreateItem(index, "has_notch_top", e.target.checked)
                          }
                        />
                        <span className="text-[11px] font-medium text-slate-600 dark:text-zink-100">
                          نک‌دار رو
                        </span>
                      </label>
                      <label
                        htmlFor={`create-notch-bottom-${index}`}
                        className="inline-flex items-center gap-2 px-2.5 py-1.5 transition-colors border rounded-lg cursor-pointer select-none border-slate-300 dark:border-zink-500 hover:border-custom-400"
                      >
                        <input
                          type="checkbox"
                          id={`create-notch-bottom-${index}`}
                          className="w-4 h-4 rounded cursor-pointer text-custom-500 border-slate-300 dark:border-zink-500 focus:ring-custom-500"
                          checked={item.has_notch_bottom ?? true}
                          onChange={(e) =>
                            updateCreateItem(index, "has_notch_bottom", e.target.checked)
                          }
                        />
                        <span className="text-[11px] font-medium text-slate-600 dark:text-zink-100">
                          نک‌دار زیر
                        </span>
                      </label>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2.5">
                      <div>
                        <label className="block text-[10px] font-medium text-slate-500 dark:text-zink-300 mb-0.5">
                          کالا/خدمت <span className="text-red-400">*</span>
                        </label>
                        <Select
                          options={goodsOptions}
                          onInputChange={setGoodsQuery}
                          value={
                            item.goods_service_id
                              ? {
                                value: String(item.goods_service_id),
                                label: getGoodsLabel(item.goods_service_id),
                              }
                              : null
                          }
                          onChange={(opt: any) => {
                            updateCreateItem(index, "goods_service_id", opt?.value || "");
                          }}
                          placeholder="انتخاب..."
                          styles={selectStyles}
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          isClearable
                          noOptionsMessage={() => "موردی یافت نشد"}
                        />
                        {createValidation.touched.items &&
                          createValidation.errors.items &&
                          typeof createValidation.errors.items === "object" &&
                          (createValidation.errors.items as any)[index]?.goods_service_id && (
                            <p className="text-red-500 text-[10px] mt-0.5 flex items-center gap-1">
                              <span className="inline-block w-1 h-1 bg-red-500 rounded-full"></span>
                              {(createValidation.errors.items as any)[index].goods_service_id}
                            </p>
                          )}
                      </div>


                      <div>
                        <label className="block text-[10px] font-medium text-slate-500 dark:text-zink-300 mb-0.5">
                          تعداد <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          className="form-input w-full text-xs rounded-lg border-slate-300 dark:border-zink-500 bg-white dark:bg-zink-600 focus:border-custom-500 focus:ring-custom-500 h-8"
                          value={item.quantity}
                          onChange={(e) => {
                            const val = e.target.value.replace(/,/g, "");
                            if (val === "" || /^\d*$/.test(val)) {
                              updateCreateItem(index, "quantity", val ? Number(val).toLocaleString("en-US") : "");
                            }
                          }}
                          placeholder="۰"
                        />
                        {createValidation.touched.items &&
                          createValidation.errors.items &&
                          typeof createValidation.errors.items === "object" &&
                          (createValidation.errors.items as any)[index]?.quantity && (
                            <p className="text-red-500 text-[10px] mt-0.5 flex items-center gap-1">
                              <span className="inline-block w-1 h-1 bg-red-500 rounded-full"></span>
                              {(createValidation.errors.items as any)[index].quantity}
                            </p>
                          )}
                      </div>

                      <div>
                        <label className="block text-[10px] font-medium text-slate-500 dark:text-zink-300 mb-0.5">
                          طول <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          className="form-input w-full text-xs rounded-lg border-slate-300 dark:border-zink-500 bg-white dark:bg-zink-600 focus:border-custom-500 focus:ring-custom-500 h-8"
                          value={item.length}
                          onChange={(e) => {
                            const val = e.target.value.replace(/,/g, "");
                            if (val === "" || /^\d*\.?\d*$/.test(val)) {
                              updateCreateItem(index, "length", formatLiveAmount(val));
                            }
                          }}
                          placeholder="۰"
                        />
                        {createValidation.touched.items &&
                          createValidation.errors.items &&
                          typeof createValidation.errors.items === "object" &&
                          (createValidation.errors.items as any)[index]?.length && (
                            <p className="text-red-500 text-[10px] mt-0.5 flex items-center gap-1">
                              <span className="inline-block w-1 h-1 bg-red-500 rounded-full"></span>
                              {(createValidation.errors.items as any)[index].length}
                            </p>
                          )}
                      </div>

                      <div>
                        <label className="block text-[10px] font-medium text-slate-500 dark:text-zink-300 mb-0.5">
                          عرض <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          className="form-input w-full text-xs rounded-lg border-slate-300 dark:border-zink-500 bg-white dark:bg-zink-600 focus:border-custom-500 focus:ring-custom-500 h-8"
                          value={item.width}
                          onChange={(e) => {
                            const val = e.target.value.replace(/,/g, "");
                            if (val === "" || /^\d*\.?\d*$/.test(val)) {
                              updateCreateItem(index, "width", formatLiveAmount(val));
                            }
                          }}
                          placeholder="۰"
                        />
                        {createValidation.touched.items &&
                          createValidation.errors.items &&
                          typeof createValidation.errors.items === "object" &&
                          (createValidation.errors.items as any)[index]?.width && (
                            <p className="text-red-500 text-[10px] mt-0.5 flex items-center gap-1">
                              <span className="inline-block w-1 h-1 bg-red-500 rounded-full"></span>
                              {(createValidation.errors.items as any)[index].width}
                            </p>
                          )}
                      </div>

                      <div>
                        <label className="block text-[10px] font-medium text-slate-500 dark:text-zink-300 mb-0.5">
                          قیمت واحد <span className="text-red-400">*</span>
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          className="form-input w-full text-xs rounded-lg border-slate-300 dark:border-zink-500 bg-white dark:bg-zink-600 focus:border-custom-500 focus:ring-custom-500 h-8"
                          value={item.unit_price}
                          onChange={(e) => {
                            const val = e.target.value.replace(/,/g, "");
                            if (val === "" || /^\d*\.?\d*$/.test(val)) {
                              updateCreateItem(index, "unit_price", formatLiveAmount(val));
                            }
                          }}
                          placeholder="۰"
                        />
                        {createValidation.touched.items &&
                          createValidation.errors.items &&
                          typeof createValidation.errors.items === "object" &&
                          (createValidation.errors.items as any)[index]?.unit_price && (
                            <p className="text-red-500 text-[10px] mt-0.5 flex items-center gap-1">
                              <span className="inline-block w-1 h-1 bg-red-500 rounded-full"></span>
                              {(createValidation.errors.items as any)[index].unit_price}
                            </p>
                          )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {createValidation.touched.items &&
                typeof createValidation.errors.items === "string" && (
                  <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                    <span className="inline-block w-1 h-1 bg-red-500 rounded-full"></span>
                    {createValidation.errors.items}
                  </p>
                )}

              {createValidation.values.items.length > 0 && (
                <div className="mt-4 rounded-lg overflow-hidden shadow-sm bg-white dark:bg-zink-600/30 border border-slate-200 dark:border-zink-500">
                  <div className="bg-slate-100 dark:bg-zink-600/40 px-3 py-2 text-xs font-semibold text-slate-700 dark:text-zink-100 flex items-center gap-2">
                    <span className="inline-block w-1 h-3 bg-custom-500 rounded-full"></span>
                    لیست کالاها/خدمات
                  </div>
                  <div className="divide-y divide-slate-200/60 dark:divide-zink-500/60">
                    {createValidation.values.items.map((item, idx) => {
                      const goodsLabel = getGoodsLabel(item.goods_service_id);
                      return (
                        <div
                          key={idx}
                          className="flex items-center justify-between px-3 py-2 text-xs hover:bg-slate-50 dark:hover:bg-zink-700/30 transition-colors"
                        >
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                            <span className="font-medium text-slate-800 dark:text-zink-100">
                              {goodsLabel || "نامشخص"}
                            </span>
                            <span className="text-slate-500 dark:text-zink-300">
                              تعداد: <span className="font-mono">{item.quantity || "۰"}</span>
                            </span>
                            <span className="text-slate-500 dark:text-zink-300">
                              طول: <span className="font-mono">{item.length || "۰"}</span>
                            </span>
                            <span className="text-slate-500 dark:text-zink-300">
                              عرض: <span className="font-mono">{item.width || "۰"}</span>
                            </span>
                            <span className="text-slate-500 dark:text-zink-300">
                              قیمت: <span className="font-mono">{item.unit_price || "۰"}</span>
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeCreateItem(idx)}
                            className="text-slate-400 hover:text-red-500 transition-colors p-0.5 rounded-full hover:bg-red-50 dark:hover:bg-red-900/20"
                          >
                            <Trash2 className="size-3" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </form>
        </Modal.Body>

        <Modal.Footer className="flex justify-end items-center gap-3 p-3.5 border-t border-slate-200 dark:border-zink-500 bg-slate-50/80 dark:bg-zink-700/50 rounded-b-lg">
          <button
            type="button"
            onClick={() => setShowCreateModal(false)}
            className="btn bg-slate-200 dark:bg-zink-500 text-slate-700 dark:text-zink-100 hover:bg-slate-300 dark:hover:bg-zink-400 px-5 py-2 text-sm font-medium transition-colors"
          >
            انصراف
          </button>
          <button
            type="button"
            onClick={() => createValidation.handleSubmit()}
            className="inline-flex items-center justify-center px-6 py-2 text-sm font-semibold text-white bg-custom-500 border border-custom-500 rounded-lg shadow-sm hover:bg-custom-600 hover:shadow-md transition-all duration-200 focus:ring-2 focus:ring-custom-500/50"
          >
            ثبت پیش‌فاکتور
          </button>
        </Modal.Footer>
      </Modal>

      {/* ========== مودال تأیید نهایی ========== */}
      <Modal
        show={showCreateConfirm}
        onHide={() => setShowCreateConfirm(false)}
        modal-center="true"
        dialogClassName="w-screen md:w-[46rem]"
        backdropClassName="backdrop-blur-lg"
      >
        <Modal.Header
          className="flex items-center justify-between p-4 border-b dark:border-zink-500"
          closeButtonClass="transition-all duration-200 ease-linear text-blue-800 dark:text-white hover:text-red-500 dark:hover:text-red-400"
        >
          <Modal.Title className="text-16 font-bold">تأیید نهایی پیش‌فاکتور</Modal.Title>
        </Modal.Header>
        <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
          <div className="space-y-4" dir="rtl">
            <p className="text-sm text-slate-500 dark:text-zink-300">
              لطفاً کالاهای زیر را پیش از ثبت نهایی بررسی کنید.
            </p>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-3 bg-slate-100 dark:bg-zink-700 rounded-lg text-sm">
              <div className="flex flex-col text-right">
                <span className="text-xs text-black dark:text-zink-400">رابط</span>
                <span className="font-medium text-slate-700 dark:text-zink-100">
                  {contactLabel(createValidation.values.contact_id) || "-"}
                </span>
              </div>
              <div className="flex flex-col text-right">
                <span className="text-xs text-black dark:text-zink-400">مشتری</span>
                <span className="font-medium text-slate-700 dark:text-zink-100">
                  {getCustomerFullName(customers.find((c) => String(c.id) === createValidation.values.customer_id)) || "-"}
                </span>
              </div>
              <div className="flex flex-col text-right">
                <span className="text-xs text-black dark:text-zink-400">درصد پورسانت</span>
                <span className="font-medium text-slate-700 dark:text-zink-100">
                  {/* مقدار صفر معتبر است، پس صریح بررسی می‌شود نه با truthy بودن. */}
                  {createValidation.values.contact_id &&
                    String(createValidation.values.commission_percentage).trim() !== ""
                    ? `${Number(createValidation.values.commission_percentage)}٪`
                    : "-"}
                </span>
              </div>
              <div className="flex flex-col text-right">
                <span className="text-xs text-black dark:text-zink-400">تاریخ فاکتور</span>
                <span className="font-medium text-slate-700 dark:text-zink-100">{createValidation.values.invoice_date}</span>
              </div>
              <div className="flex flex-col text-right">
                <span className="text-xs text-black dark:text-zink-400">تاریخ اعتبار</span>
                <span className="font-medium text-slate-700 dark:text-zink-100">{createValidation.values.valid_until}</span>
              </div>
              <div className="flex flex-col text-right">
                <span className="text-xs text-black dark:text-zink-400">نوع فروش</span>
                <span className="font-medium text-slate-700 dark:text-zink-100">
                  {saleTypeOptions.find((o) => o.value === createValidation.values.sale_type)?.label || "-"}
                </span>
              </div>
              <div className="flex flex-col text-right">
                <span className="text-xs text-black dark:text-zink-400">ارز</span>
                <span className="font-medium text-slate-700 dark:text-zink-100">
                  {currencies.find((c) => String(c.id) === String(createValidation.values.currency_id))?.name || "-"}
                </span>
              </div>
              <div className="flex flex-col text-right">
                <span className="text-xs text-black dark:text-zink-400">تعداد کالا/خدمت</span>
                <span className="font-medium text-slate-700 dark:text-zink-100">{createValidation.values.items.length}</span>
              </div>
            </div>

            <div className="space-y-2.5">
              {createValidation.values.items.map((item, idx) => {
                const goodsRecord = getGoodsRecord(item.goods_service_id);
                const panelSpec = decodeGoodsCode(goodsRecord?.code);
                return (
                  <div
                    key={idx}
                    className="rounded-lg border border-slate-200 dark:border-zink-500 overflow-hidden"
                  >
                    <div className="px-3 py-2 bg-slate-100 dark:bg-zink-700 text-sm font-semibold text-slate-700 dark:text-zink-100">
                      {idx + 1}. {getGoodsLabel(item.goods_service_id) || "کالا/خدمت نامشخص"}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 text-sm">
                      <div className="flex flex-col text-right">
                        <span className="text-xs text-black dark:text-zink-400">تعداد</span>
                        <span className="font-medium text-slate-700 dark:text-zink-100">{item.quantity || "-"}</span>
                      </div>
                      <div className="flex flex-col text-right">
                        <span className="text-xs text-black dark:text-zink-400">طول</span>
                        <span className="font-medium text-slate-700 dark:text-zink-100">{item.length || "-"}</span>
                      </div>
                      <div className="flex flex-col text-right">
                        <span className="text-xs text-black dark:text-zink-400">عرض</span>
                        <span className="font-medium text-slate-700 dark:text-zink-100">{item.width || "-"}</span>
                      </div>
                      <div className="flex flex-col text-right">
                        <span className="text-xs text-black dark:text-zink-400">فی (قیمت واحد)</span>
                        <span className="font-medium text-slate-700 dark:text-zink-100">{item.unit_price || "-"}</span>
                      </div>
                    </div>
                    {panelSpec && (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-3 border-t border-slate-200 dark:border-zink-500 bg-amber-50 dark:bg-zink-700 text-sm">
                        <div className="flex flex-col text-right">
                          <span className="text-xs text-black dark:text-zink-400">نوع پانل</span>
                          <span className="font-medium text-slate-700 dark:text-zink-100">{panelSpec.panelType}</span>
                        </div>
                        {/* «نک‌دار رو» و «نک‌دار زیر» همین قلم؛ مستقیم از پاسخ سرور.
                            رکوردهای قدیمی فقط has_notch دارند و همان جایگزین می‌شود. */}
                        <div className="flex flex-col text-right">
                          <span className="text-xs text-black dark:text-zink-400">نک‌دار رو</span>
                          <span className="font-medium text-slate-700 dark:text-zink-100">
                            {notchLabel(item.has_notch_top ?? item.has_notch)}
                          </span>
                        </div>
                        <div className="flex flex-col text-right">
                          <span className="text-xs text-black dark:text-zink-400">نک‌دار زیر</span>
                          <span className="font-medium text-slate-700 dark:text-zink-100">
                            {notchLabel(item.has_notch_bottom ?? item.has_notch)}
                          </span>
                        </div>
                        <div className="flex flex-col text-right">
                          <span className="text-xs text-black dark:text-zink-400">ضخامت</span>
                          <span className="font-medium text-slate-700 dark:text-zink-100">{(Number(panelSpec.thickness) / 100).toFixed(2)}</span>
                        </div>
                        <div className="flex flex-col text-right">
                          <span className="text-xs text-black dark:text-zink-400">ورق رو</span>
                          <span className="font-medium text-slate-700 dark:text-zink-100">{panelSpec.topSheet}</span>
                        </div>
                        <div className="flex flex-col text-right">
                          <span className="text-xs text-black dark:text-zink-400">ورق پشت</span>
                          <span className="font-medium text-slate-700 dark:text-zink-100">{panelSpec.bottomSheet}</span>
                        </div>
                        <div className="flex flex-col text-right">
                          <span className="text-xs text-black dark:text-zink-400">ضخامت ورق رو</span>
                          <span dir="ltr" style={{ direction: 'ltr', unicodeBidi: 'bidi-override' }} className="font-medium text-slate-700 dark:text-zink-100 text-right">{panelSpec.topSheetThickness}</span>
                        </div>
                        <div className="flex flex-col text-right">
                          <span className="text-xs text-black dark:text-zink-400">ضخامت ورق پشت</span>
                          <span dir="ltr" style={{ direction: 'ltr', unicodeBidi: 'bidi-override' }} className="font-medium text-slate-700 dark:text-zink-100 text-right">{panelSpec.bottomSheetThickness}</span>
                        </div>
                        <div className="flex flex-col text-right">
                          <span className="text-xs text-black dark:text-zink-400">دانسیته فوم</span>
                          <span dir="ltr" style={{ direction: 'ltr', unicodeBidi: 'bidi-override' }} className="font-medium text-slate-700 dark:text-zink-100 text-right">{panelSpec.foamDensity}</span>
                        </div>
                        <div className="flex flex-col text-right">
                          <span className="text-xs text-black dark:text-zink-400">نوع فوم</span>
                          <span className="font-medium text-slate-700 dark:text-zink-100">{panelSpec.foamType}</span>
                        </div>
                        <div className="flex flex-col text-right">
                          <span className="text-xs text-black dark:text-zink-400">رنگ ورق رو</span>
                          <span className="font-medium text-slate-700 dark:text-zink-100">{panelSpec.topColor}</span>
                        </div>
                        <div className="flex flex-col text-right">
                          <span className="text-xs text-black dark:text-zink-400">رنگ ورق زیر</span>
                          <span className="font-medium text-slate-700 dark:text-zink-100">{panelSpec.bottomColor}</span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer className="flex justify-end items-center gap-3 p-3.5 border-t border-slate-200 dark:border-zink-500 bg-slate-50 dark:bg-zink-700 rounded-b-lg">
          <button
            type="button"
            onClick={() => setShowCreateConfirm(false)}
            disabled={createSubmitting}
            className="px-5 py-2 text-sm font-medium text-slate-600 dark:text-zink-300 hover:text-slate-800 dark:hover:text-zink-100 transition-colors disabled:opacity-60"
          >
            بازگشت و ویرایش
          </button>
          <button
            type="button"
            onClick={submitCreateInvoice}
            disabled={createSubmitting}
            className="inline-flex items-center justify-center gap-2 px-6 py-2 text-sm font-semibold text-white bg-custom-500 border border-custom-500 rounded-lg shadow-sm hover:bg-custom-600 hover:shadow-md transition-all duration-200 focus:ring-2 focus:ring-custom-500/50 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {createSubmitting ? (
              <>
                <Loader2 className="size-4 animate-spin" />
                در حال ثبت...
              </>
            ) : (
              "تأیید و ثبت نهایی"
            )}
          </button>
        </Modal.Footer>
      </Modal>

      {/* ========== مودال ویرایش ========== */}
      <Modal
        show={showEditModal}
        onHide={() => {
          setShowEditModal(false);
          setEditingId(null);
          setEditingInvoice(null);
          editValidation.resetForm();
        }}
        modal-center="true"
        dialogClassName="w-screen md:w-[48rem]"
      >
        <Modal.Header
          className="flex items-center justify-between p-4 border-b dark:border-zink-500"
          closeButtonClass="transition-all duration-200 ease-linear text-blue-800 dark:text-white hover:text-red-500 dark:hover:text-red-400"
        >
          <Modal.Title className="text-16">ویرایش پیش‌فاکتور</Modal.Title>
        </Modal.Header>
        <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
          <form onSubmit={editValidation.handleSubmit}>
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2 mb-6">
              {/* ترتیب مستند نسخه ۵: مشتری، رابط، درصد پورسانت. */}
              <div>
                <label className="block text-base font-medium mb-1">مشتری:</label>
                <Select
                  options={customerSearch.options}
                  onInputChange={customerSearch.onInputChange}
                  value={
                    editValidation.values.customer_id
                      ? {
                        value: editValidation.values.customer_id,
                        label: getCustomerFullName(
                          customers.find((c) => String(c.id) === editValidation.values.customer_id)
                        ),
                      }
                      : null
                  }
                  onChange={(opt: any) =>
                    editValidation.setFieldValue("customer_id", opt?.value || "")
                  }
                  placeholder="جست‌وجوی مشتری..."
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  isClearable
                  noOptionsMessage={() => "موردی یافت نشد"}
                />
                {editValidation.touched.customer_id &&
                  editValidation.errors.customer_id && (
                    <p className="text-red-400 mt-1 text-sm">
                      {typeof editValidation.errors.customer_id === "string"
                        ? editValidation.errors.customer_id
                        : ""}
                    </p>
                  )}
              </div>
              <div>
                <label className="block text-base font-medium mb-1">رابط:</label>
                <Select
                  options={contactSearch.options}
                  onInputChange={contactSearch.onInputChange}
                  value={
                    editValidation.values.contact_id
                      ? {
                        value: editValidation.values.contact_id,
                        label: contactLabel(editValidation.values.contact_id),
                      }
                      : null
                  }
                  onChange={(opt: any) => {
                    const next = opt?.value || "";
                    editValidation.setFieldValue("contact_id", next);
                    if (!next) editValidation.setFieldValue("commission_percentage", "");
                  }}
                  // رابط و درصدِ تأییدشده تا وقتی تأیید برداشته نشود قابل تغییر نیستند.
                  isDisabled={commissionLocked}
                  placeholder="جست‌وجوی رابط..."
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  isClearable
                  noOptionsMessage={() => "رابطی ثبت نشده است"}
                />
              </div>

              <div>
                <label className="block text-base font-medium mb-1">درصد پورسانت:</label>
                <input
                  type="text"
                  inputMode="decimal"
                  name="commission_percentage"
                  className="form-input w-full disabled:bg-slate-100 dark:disabled:bg-zink-500"
                  disabled={!editValidation.values.contact_id || commissionLocked}
                  placeholder={
                    editValidation.values.contact_id
                      ? "بین ۰ تا ۱۰۰"
                      : "ابتدا رابط را انتخاب کنید"
                  }
                  value={editValidation.values.commission_percentage}
                  onChange={(e) =>
                    editValidation.setFieldValue(
                      "commission_percentage",
                      normalizeDigits(e.target.value)
                    )
                  }
                  onBlur={editValidation.handleBlur}
                />
                {editValidation.touched.commission_percentage &&
                  editValidation.errors.commission_percentage && (
                    <p className="text-red-400 mt-1 text-sm">
                      {String(editValidation.errors.commission_percentage)}
                    </p>
                  )}

                {/* تأیید پورسانت از ستون «تأیید پورسانت» در خود جدول انجام می‌شود تا
                    مدیر فروش هم به آن برسد؛ سرور به مدیر فروش اجازه‌ی ویرایش
                    پیش‌فاکتور نمی‌دهد. اینجا فقط وضعیت نمایش داده می‌شود. */}
                {/* مبلغ پورسانت، محاسبه‌شده از gross_amount همین سفارش. */}
                <div className="mt-2">
                  <label className="block mb-1 text-base font-medium">مبلغ پورسانت:</label>
                  <input
                    type="text"
                    readOnly
                    className="w-full cursor-default form-input bg-slate-100 dark:bg-zink-500"
                    value={formatCommissionAmount(
                      editValidation.values.contact_id
                        ? commissionAmount(
                          editingInvoice?.gross_amount,
                          editValidation.values.commission_percentage
                        )
                        : null
                    )}
                  />
                  <p className="mt-1 text-[11px] text-slate-500 dark:text-zink-300">
                    بر پایه‌ی جمع کل بدون مالیات بر ارزش افزوده
                  </p>
                </div>

                {commissionLocked && (
                  <p className="mt-2 text-[11px] leading-5 text-green-600">
                    پورسانت تأیید شده
                    {commissionApproverName(editingInvoice)
                      ? ` توسط ${commissionApproverName(editingInvoice)}`
                      : ""}
                    {editingInvoice?.commission_approved_at_jalali
                      ? `، ${ltrValue(String(editingInvoice.commission_approved_at_jalali))}`
                      : ""}
                    . برای تغییر رابط یا درصد، ابتدا تأیید را از ستون «تأیید پورسانت»
                    بردارید.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-base font-medium mb-1">ارز:</label>
                <Select
                  options={currencySearch.options}
                  onInputChange={currencySearch.onInputChange}
                  value={
                    editValidation.values.currency_id
                      ? {
                        value: editValidation.values.currency_id,
                        label:
                          currencies.find(
                            (c) => String(c.id) === editValidation.values.currency_id
                          )?.name || "",
                      }
                      : null
                  }
                  onChange={(opt: any) =>
                    editValidation.setFieldValue("currency_id", opt?.value || "")
                  }
                  placeholder="جست‌وجوی ارز..."
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  isClearable
                  noOptionsMessage={() => "موردی یافت نشد"}
                />
              </div>
              <div>
                <label className="block text-base font-medium mb-1">نوع فروش:</label>
                <Select
                  options={saleTypeOptions}
                  value={saleTypeOptions.find(
                    (s) => s.value === editValidation.values.sale_type
                  )}
                  onChange={(opt: any) =>
                    editValidation.setFieldValue("sale_type", opt?.value || "domestic")
                  }
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
              </div>
              <ModalData
                label="تاریخ:"
                data={editValidation.values.invoice_date}
                setData={(v) => editValidation.setFieldValue("invoice_date", v)}
              />
              <ModalData
                label="تاریخ اعتبار:"
                data={editValidation.values.valid_until}
                setData={(v) => editValidation.setFieldValue("valid_until", v)}
              />
            </div>

            <div className="border-t border-slate-200 dark:border-zink-500 pt-4">
              <div className="flex items-center justify-between mb-1">
                <h5 className="text-15 font-semibold">کالا/خدمت‌ها</h5>
                <button
                  type="button"
                  disabled={commissionLocked}
                  onClick={addEditItem}
                  className="inline-flex items-center gap-1 text-sm text-custom-500 hover:text-custom-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Plus className="size-4" />
                  افزودن کالا یا خدمت
                </button>
              </div>
              <p className="text-xs text-slate-500 dark:text-zink-300 mb-3">
                تغییرات هر قلم جداگانه روی سرور ثبت می‌شود؛ پس از ویرایش، دکمهٔ ذخیرهٔ همان قلم را بزنید.
              </p>

              {editValidation.values.items.map((item, index) => (
                <fieldset disabled={commissionLocked} key={item.id ?? `new-${index}`} className="bg-slate-50 dark:bg-zink-700/20 p-3 rounded-lg mb-3 border border-slate-200 dark:border-zink-500 disabled:opacity-70">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium">{getItemLabel(item, index)}</span>
                      {item.item_status && (
                        <span
                          className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${ITEM_STATUS_CLASSES[String(item.item_status)] ||
                            "bg-gray-100 text-gray-800 dark:bg-gray-500/20 dark:text-gray-400"
                            }`}
                        >
                          {ITEM_STATUS_LABELS[String(item.item_status)] || item.item_status}
                        </span>
                      )}
                      {!item.id && (
                        <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-400">
                          ثبت‌نشده
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={itemSubmittingIndex !== null}
                        onClick={() => (item.id ? handleUpdateItem(index) : handleCreateItem(index))}
                        className="inline-flex items-center gap-1 text-xs font-medium text-custom-600 hover:text-custom-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {itemSubmittingIndex === index ? (
                          <Loader2 className="size-3.5 animate-spin" />
                        ) : (
                          <CheckCircle2 className="size-3.5" />
                        )}
                        {item.id ? "ذخیرهٔ قلم" : "ثبت قلم"}
                      </button>
                      <button
                        type="button"
                        disabled={itemSubmittingIndex !== null}
                        onClick={() => handleDeleteItem(index)}
                        className="text-red-500 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Trash2 className="size-4" />
                      </button>
                    </div>
                  </div>
                  {/* «نک‌دار رو» و «نک‌دار زیر» بالای فیلدهای قلم، مثل فرم ایجاد. */}
                  <div className="flex flex-wrap gap-2 mb-3">
                    <label
                      htmlFor={`edit-notch-top-${index}`}
                      className="inline-flex items-center gap-2 px-2.5 py-1.5 transition-colors border rounded-lg cursor-pointer select-none border-slate-300 dark:border-zink-500 hover:border-custom-400"
                    >
                      <input
                        type="checkbox"
                        id={`edit-notch-top-${index}`}
                        className="w-4 h-4 rounded cursor-pointer text-custom-500 border-slate-300 dark:border-zink-500 focus:ring-custom-500"
                        checked={item.has_notch_top ?? true}
                        onChange={(e) => updateEditItem(index, "has_notch_top", e.target.checked)}
                      />
                      <span className="text-xs font-medium text-slate-600 dark:text-zink-100">
                        نک‌دار رو
                      </span>
                    </label>
                    <label
                      htmlFor={`edit-notch-bottom-${index}`}
                      className="inline-flex items-center gap-2 px-2.5 py-1.5 transition-colors border rounded-lg cursor-pointer select-none border-slate-300 dark:border-zink-500 hover:border-custom-400"
                    >
                      <input
                        type="checkbox"
                        id={`edit-notch-bottom-${index}`}
                        className="w-4 h-4 rounded cursor-pointer text-custom-500 border-slate-300 dark:border-zink-500 focus:ring-custom-500"
                        checked={item.has_notch_bottom ?? true}
                        onChange={(e) => updateEditItem(index, "has_notch_bottom", e.target.checked)}
                      />
                      <span className="text-xs font-medium text-slate-600 dark:text-zink-100">
                        نک‌دار زیر
                      </span>
                    </label>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div>
                      <label className="block text-xs font-medium mb-1">کالا/خدمت:</label>
                      <Select
                        options={goodsOptions}
                        onInputChange={setGoodsQuery}
                        value={
                          item.goods_service_id
                            ? {
                              value: String(item.goods_service_id),
                              label: getGoodsLabel(item.goods_service_id),
                            }
                            : null
                        }
                        onChange={(opt: any) => {
                          updateEditItem(index, "goods_service_id", opt?.value || "");
                        }}
                        placeholder="انتخاب کالا..."
                        styles={selectStyles}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                        isClearable
                        noOptionsMessage={() => "موردی یافت نشد"}
                      />
                      {editValidation.touched.items &&
                        editValidation.errors.items &&
                        typeof editValidation.errors.items === "object" &&
                        (editValidation.errors.items as any)[index]?.goods_service_id && (
                          <p className="text-red-400 mt-1 text-xs">
                            {(editValidation.errors.items as any)[index].goods_service_id}
                          </p>
                        )}
                    </div>


                    <div>
                      <label className="block text-xs font-medium mb-1">تعداد:</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        className="form-input w-full text-sm"
                        value={item.quantity}
                        onChange={(e) => {
                          const val = e.target.value.replace(/,/g, "");
                          if (val === "" || /^\d*$/.test(val)) {
                            updateEditItem(index, "quantity", val ? Number(val).toLocaleString("en-US") : "");
                          }
                        }}
                      />
                      {editValidation.touched.items &&
                        editValidation.errors.items &&
                        typeof editValidation.errors.items === "object" &&
                        (editValidation.errors.items as any)[index]?.quantity && (
                          <p className="text-red-400 mt-1 text-xs">
                            {(editValidation.errors.items as any)[index].quantity}
                          </p>
                        )}
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">طول:</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        className="form-input w-full text-sm"
                        value={item.length}
                        onChange={(e) => {
                          const val = e.target.value.replace(/,/g, "");
                          if (val === "" || /^\d*\.?\d*$/.test(val)) {
                            updateEditItem(index, "length", formatLiveAmount(val));
                          }
                        }}
                      />
                      {editValidation.touched.items &&
                        editValidation.errors.items &&
                        typeof editValidation.errors.items === "object" &&
                        (editValidation.errors.items as any)[index]?.length && (
                          <p className="text-red-400 mt-1 text-xs">
                            {(editValidation.errors.items as any)[index].length}
                          </p>
                        )}
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">عرض:</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        className="form-input w-full text-sm"
                        value={item.width}
                        onChange={(e) => {
                          const val = e.target.value.replace(/,/g, "");
                          if (val === "" || /^\d*\.?\d*$/.test(val)) {
                            updateEditItem(index, "width", formatLiveAmount(val));
                          }
                        }}
                      />
                      {editValidation.touched.items &&
                        editValidation.errors.items &&
                        typeof editValidation.errors.items === "object" &&
                        (editValidation.errors.items as any)[index]?.width && (
                          <p className="text-red-400 mt-1 text-xs">
                            {(editValidation.errors.items as any)[index].width}
                          </p>
                        )}
                    </div>
                    <div>
                      <label className="block text-xs font-medium mb-1">قیمت واحد:</label>
                      <input
                        type="text"
                        inputMode="numeric"
                        className="form-input w-full text-sm"
                        value={item.unit_price}
                        onChange={(e) => {
                          const val = e.target.value.replace(/,/g, "");
                          if (val === "" || /^\d*\.?\d*$/.test(val)) {
                            updateEditItem(index, "unit_price", formatLiveAmount(val));
                          }
                        }}
                        placeholder="۰"
                      />
                      {editValidation.touched.items &&
                        editValidation.errors.items &&
                        typeof editValidation.errors.items === "object" &&
                        (editValidation.errors.items as any)[index]?.unit_price && (
                          <p className="text-red-400 mt-1 text-xs">
                            {(editValidation.errors.items as any)[index].unit_price}
                          </p>
                        )}
                    </div>
                  </div>
                </fieldset>
              ))}

              {editValidation.values.items.length > 0 && (
                <div className="mt-4 border border-slate-200 dark:border-zink-500 rounded-lg overflow-hidden">
                  <div className="bg-slate-100 dark:bg-zink-600/30 px-3 py-2 text-sm font-medium">
                    لیست کالاها/خدمات
                  </div>
                  <div className="divide-y divide-slate-200 dark:divide-zink-500">
                    {editValidation.values.items.map((item, idx) => {
                      const goodsLabel = getGoodsLabel(item.goods_service_id);
                      return (
                        <div key={item.id ?? `new-${idx}`} className="flex items-center justify-between px-3 py-2 text-sm">
                          <div className="flex flex-wrap gap-2">
                            <span className="font-medium">
                              {goodsLabel || "نامشخص"}
                            </span>
                            <span>تعداد: {item.quantity || "۰"}</span>
                            <span>طول: {item.length || "۰"}</span>
                            <span>عرض: {item.width || "۰"}</span>
                            <span>قیمت واحد: {item.unit_price || "۰"}</span>
                          </div>
                          <span className="text-xs text-slate-500 dark:text-zink-300">
                            {item.id ? `شناسه قلم: ${item.id}` : "ثبت‌نشده"}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {editValidation.touched.items &&
                typeof editValidation.errors.items === "string" && (
                  <p className="text-red-400 mt-1 text-sm">
                    {editValidation.errors.items}
                  </p>
                )}
            </div>
          </form>
        </Modal.Body>
        <Modal.Footer className="flex justify-center p-4 border-t dark:border-zink-500">
          <button
            type="button"
            onClick={() => editValidation.handleSubmit()}
            className="btn min-w-[9rem] px-8 py-2.5 mt-2 font-bold text-white bg-custom-500 border-custom-500 hover:bg-custom-600"
          >
            ویرایش
          </button>
        </Modal.Footer>
      </Modal>

      {/* ========== مودال مشخصات سفارش (با دکمه شرح کالا) ========== */}
      {/* ========== مودال مشخصات سفارش (با دکمه شرح کالا) ========== */}
      <Modal
        show={!!showSpecsModal}
        onHide={() => {
          setShowSpecsModal(null);
          setSpecsLoading(false); // ریست لودینگ هنگام بسته شدن
        }}
        modal-center="true"
        dialogClassName="w-screen md:w-[50rem]"
      >
        <Modal.Header
          className="flex items-center justify-between p-4 border-b dark:border-zink-500"
          closeButtonClass="transition-all duration-200 ease-linear text-blue-800 dark:text-white hover:text-red-500 dark:hover:text-red-400"
        >
          <Modal.Title className="text-16 font-bold">
            جزئیات فاکتور {showSpecsModal?.invoice_number || ''}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto bg-gradient-to-b from-slate-100 to-slate-50 dark:from-zink-800 dark:to-zink-800/60">
          {/* ===== لودینگ ===== */}
          {specsLoading ? (
            <div className="flex flex-col items-center justify-center py-16">
              {/* اسپینر اینستاگرامی (سه نقطه متحرک) */}
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 bg-custom-500 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                <div className="w-3 h-3 bg-custom-500 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                <div className="w-3 h-3 bg-custom-500 rounded-full animate-bounce"></div>
              </div>
              <p className="mt-4 text-sm text-slate-500 dark:text-zink-300">در حال بارگذاری...</p>
            </div>
          ) : (
            /* ===== محتوای اصلی ===== */
            showSpecsModal && (() => {
              const statusMap: Record<string, { label: string; className: string }> = {
                proforma: { label: 'پیش‌فاکتور', className: 'bg-yellow-200 text-yellow-800 dark:bg-yellow-500/30 dark:text-yellow-300' },
                sales_order: { label: 'سفارش فروش / آماده تولید', className: 'bg-blue-200 text-blue-800 dark:bg-blue-500/30 dark:text-blue-300' },
                in_production: { label: 'در حال تولید', className: 'bg-purple-200 text-purple-800 dark:bg-purple-500/30 dark:text-purple-300' },
                produced: { label: 'تولید شده', className: 'bg-green-200 text-green-800 dark:bg-green-500/30 dark:text-green-300' },
                in_warehouse: { label: 'در انبار محصولات', className: 'bg-cyan-200 text-cyan-800 dark:bg-cyan-500/30 dark:text-cyan-300' },
                out_of_warehouse: { label: 'خارج‌شده از انبار', className: 'bg-indigo-200 text-indigo-800 dark:bg-indigo-500/30 dark:text-indigo-300' },
                loaded: { label: 'بارگیری‌شده', className: 'bg-orange-200 text-orange-800 dark:bg-orange-500/30 dark:text-orange-300' },
                delivered: { label: 'تحویل‌شده به مشتری', className: 'bg-emerald-200 text-emerald-800 dark:bg-emerald-500/30 dark:text-emerald-300' },
              };
              const statusKey = String(showSpecsModal.order_status || 'proforma');
              const statusInfo = statusMap[statusKey] || {
                label: statusKey || '-',
                className: 'bg-gray-200 text-gray-800 dark:bg-gray-500/30 dark:text-gray-300'
              };

              return (
                <div className="space-y-4" dir="rtl">
                  {/* اطلاعات کلی — ترتیب فیلدها دقیقاً مطابق مستند:
                      نام مشتری، وضعیت سفارش، وضعیت تسویه، تأیید مدیر فروش، تأیید مدیرعامل، کارشناس فروش */}
                  <div className="rounded-xl overflow-hidden border border-sky-100 dark:border-zink-600 shadow-sm">
                    <div className="flex items-center gap-2 px-4 py-2.5 bg-sky-50 dark:bg-zink-700 border-b border-sky-100 dark:border-zink-600">
                      <User2 className="size-4 text-sky-600 dark:text-sky-400" />
                      <h6 className="text-sm font-semibold text-sky-800 dark:text-sky-300">اطلاعات کلی</h6>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-4 bg-white dark:bg-zink-600/30">
                      {/* «رابط» پیش از «مشتری» و با برچسب خودش نمایش داده می‌شود تا با
                          مشتری اشتباه نشود. سفارش می‌تواند فقط یکی از این دو را داشته باشد. */}
                      <div className="flex flex-col text-right">
                        <span className="text-xs text-black dark:text-zink-400">رابط</span>
                        <span className="font-medium text-slate-700 dark:text-zink-100">{showSpecsModal.contact_name || '-'}</span>
                      </div>
                      <div className="flex flex-col text-right">
                        <span className="text-xs text-black dark:text-zink-400">نام مشتری</span>
                        <span className="font-medium text-slate-700 dark:text-zink-100">{showSpecsModal.customer_name || '-'}</span>
                      </div>
                      <div className="flex flex-col text-right">
                        <span className="text-xs text-black dark:text-zink-400">درصد پورسانت</span>
                        <span className="font-medium text-slate-700 dark:text-zink-100">
                          {/* مقدار صفر معتبر است، پس با مقایسه‌ی صریح بررسی می‌شود. */}
                          {showSpecsModal.commission_percentage === null ||
                            showSpecsModal.commission_percentage === undefined ||
                            String(showSpecsModal.commission_percentage).trim() === ''
                            ? '-'
                            : `${Number(showSpecsModal.commission_percentage)}٪`}
                        </span>
                      </div>
                      <div className="flex flex-col text-right">
                        <span className="text-xs text-black dark:text-zink-400">تأیید پورسانت</span>
                        {showSpecsModal.commission_approved ? (
                          <span className="font-medium text-green-500">
                            تأیید شده
                            {commissionApproverName(showSpecsModal)
                              ? ` توسط ${commissionApproverName(showSpecsModal)}`
                              : ''}
                            {showSpecsModal.commission_approved_at_jalali
                              ? `، ${ltrValue(String(showSpecsModal.commission_approved_at_jalali))}`
                              : ''}
                          </span>
                        ) : (
                          <span className="font-medium text-red-500">تأیید نشده</span>
                        )}
                      </div>
                      <div className="flex flex-col text-right">
                        <span className="text-xs text-black dark:text-zink-400">وضعیت سفارش</span>
                        <span className={`font-medium px-2 py-0.5 rounded-full text-xs inline-block w-fit ${statusInfo.className}`}>
                          {statusInfo.label}
                        </span>
                      </div>
                      <div className="flex flex-col text-right">
                        <span className="text-xs text-black dark:text-zink-400">وضعیت تسویه</span>
                        <span className={`font-medium ${isSettled(showSpecsModal) ? 'text-green-500' : 'text-red-500'}`}>
                          {isSettled(showSpecsModal) ? 'تسویه شده' : 'تسویه نشده'}
                        </span>
                      </div>
                      <div className="flex flex-col text-right">
                        <span className="text-xs text-black dark:text-zink-400">تأیید مدیر فروش</span>
                        <span className={`font-medium ${showSpecsModal.sales_manager_approved ? 'text-green-500' : 'text-red-500'}`}>
                          {showSpecsModal.sales_manager_approved ? 'تأیید شده' : 'تأیید نشده'}
                        </span>
                      </div>
                      <div className="flex flex-col text-right">
                        <span className="text-xs text-black dark:text-zink-400">تأیید مدیرعامل</span>
                        <span className={`font-medium ${showSpecsModal.ceo_approved ? 'text-green-500' : 'text-red-500'}`}>
                          {showSpecsModal.ceo_approved ? 'تأیید شده' : 'تأیید نشده'}
                        </span>
                      </div>
                      <div className="flex flex-col text-right">
                        <span className="text-xs text-black dark:text-zink-400">کارشناس فروش</span>
                        <span className="font-medium text-slate-700 dark:text-zink-100">{getSalesExpertName(showSpecsModal)}</span>
                      </div>
                      {/* ارز انتخاب‌شده‌ی سفارش؛ مقدار از «ثبت ارز» می‌آید و سرور آن را
                          در پاسخ جزئیات پیش‌فاکتور به‌صورت currency_name برمی‌گرداند. */}
                      <div className="flex flex-col text-right">
                        <span className="text-xs text-black dark:text-zink-400">ارز</span>
                        <span className="font-medium text-slate-700 dark:text-zink-100">
                          {showSpecsModal.currency_name || '-'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* جمع‌ها و مبالغ — ترتیب دقیقاً مطابق مستند:
                      کل متراژ، کل طول، هزینه بسته‌بندی، مالیات بر ارزش‌افزوده، مبلغ خالص،
                      مبلغ واریزی، واریزی تأییدشده، مانده بدهی */}
                  <div className="rounded-xl overflow-hidden border border-emerald-100 dark:border-zink-600 shadow-sm">
                    <div className="flex items-center gap-2 px-4 py-2.5 bg-emerald-50 dark:bg-zink-700 border-b border-emerald-100 dark:border-zink-600">
                      <Wallet className="size-4 text-emerald-600 dark:text-emerald-400" />
                      <h6 className="text-sm font-semibold text-emerald-800 dark:text-emerald-300">جمع‌ها و مبالغ</h6>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-4 bg-white dark:bg-zink-600/30">
                      <div className="flex flex-col text-right">
                        <span className="text-xs text-black dark:text-zink-400">کل متراژ</span>
                        <span className="font-medium text-slate-700 dark:text-zink-100">
                          {Number(showSpecsModal.total_area || 0).toLocaleString('en-US')} متر مربع
                        </span>
                      </div>
                      <div className="flex flex-col text-right">
                        <span className="text-xs text-black dark:text-zink-400">کل طول</span>
                        <span className="font-medium text-slate-700 dark:text-zink-100">
                          {Number(showSpecsModal.total_length || 0).toLocaleString('en-US')} متر
                        </span>
                      </div>
                      <div className="flex flex-col text-right">
                        <span className="text-xs text-black dark:text-zink-400">هزینه بسته‌بندی</span>
                        <span className="font-medium text-slate-700 dark:text-zink-100">
                          {Number(showSpecsModal.packaging_cost || 0).toLocaleString('en-US')} ریال
                        </span>
                      </div>
                      <div className="flex flex-col text-right">
                        <span className="text-xs text-black dark:text-zink-400">مالیات بر ارزش‌افزوده</span>
                        <span className="font-medium text-slate-700 dark:text-zink-100">
                          {Number(showSpecsModal.vat_amount || 0).toLocaleString('en-US')} ریال
                        </span>
                      </div>
                      <div className="flex flex-col text-right">
                        <span className="text-xs text-black dark:text-zink-400">مبلغ خالص</span>
                        <span className="font-bold text-custom-500 text-base">
                          {Number(showSpecsModal.net_amount || 0).toLocaleString('en-US')} ریال
                        </span>
                      </div>
                      <div className="flex flex-col text-right">
                        <span className="text-xs text-black dark:text-zink-400">مبلغ واریزی</span>
                        <span className="font-medium text-slate-700 dark:text-zink-100">
                          {Number(showSpecsModal.deposited_amount || 0).toLocaleString('en-US')} ریال
                        </span>
                      </div>
                      <div className="flex flex-col text-right">
                        <span className="text-xs text-black dark:text-zink-400">درصد واریزی</span>
                        <span className="font-medium text-slate-700 dark:text-zink-100">
                          {formatDepositPercentage(showSpecsModal.deposited_percentage)}
                        </span>
                      </div>
                      <div className="flex flex-col text-right">
                        <span className="text-xs text-black dark:text-zink-400">واریزی تأییدشده</span>
                        <span className="font-medium text-slate-700 dark:text-zink-100">
                          {Number(showSpecsModal.confirmed_deposited_amount || 0).toLocaleString('en-US')} ریال
                        </span>
                      </div>
                      <div className="flex flex-col text-right">
                        <span className="text-xs text-black dark:text-zink-400">مانده بدهی</span>
                        <span className="font-medium text-red-500 dark:text-red-400">
                          {Number(showSpecsModal.remaining_balance || 0).toLocaleString('en-US')} ریال
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* تاریخ‌ها — ترتیب دقیقاً مطابق مستند: شماره فاکتور، تاریخ فاکتور، تاریخ ایجاد، تاریخ آخرین بروزرسانی */}
                  <div className="rounded-xl overflow-hidden border border-violet-100 dark:border-zink-600 shadow-sm">
                    <div className="flex items-center gap-2 px-4 py-2.5 bg-violet-50 dark:bg-zink-700 border-b border-violet-100 dark:border-zink-600">
                      <CalendarClock className="size-4 text-violet-600 dark:text-violet-400" />
                      <h6 className="text-sm font-semibold text-violet-800 dark:text-violet-300">تاریخ‌ها</h6>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 bg-white dark:bg-zink-600/30">
                      <div className="flex flex-col text-right">
                        <span className="text-xs text-black dark:text-zink-400">شماره فاکتور</span>
                        <span className="font-mono font-medium text-slate-700 dark:text-zink-100">
                          {showSpecsModal.invoice_number || '-'}
                        </span>
                      </div>
                      <div className="flex flex-col text-right">
                        <span className="text-xs text-black dark:text-zink-400">تاریخ فاکتور</span>
                        <span className="font-medium text-slate-700 dark:text-zink-100">
                          {showSpecsModal.invoice_date || '-'}
                        </span>
                      </div>
                      <div className="flex flex-col text-right">
                        <span className="text-xs text-black dark:text-zink-400">تاریخ ایجاد</span>
                        <span className="font-medium text-slate-700 dark:text-zink-100">
                          {showSpecsModal.created_at_jalali || '-'}
                        </span>
                      </div>
                      <div className="flex flex-col text-right">
                        <span className="text-xs text-black dark:text-zink-400">تاریخ آخرین بروزرسانی</span>
                        <span className="font-medium text-slate-700 dark:text-zink-100">
                          {showSpecsModal.updated_at_jalali || '-'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* ===== لیست کالاها با دکمه شرح ===== */}
                  <div className="rounded-xl overflow-hidden border border-amber-100 dark:border-zink-600 shadow-sm">
                    <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 dark:bg-zink-700 border-b border-amber-100 dark:border-zink-600">
                      <Package className="size-4 text-amber-600 dark:text-amber-400" />
                      <h6 className="text-sm font-semibold text-amber-800 dark:text-amber-300">لیست کالاها/خدمات</h6>
                    </div>
                    <div className="p-4 bg-white dark:bg-zink-600/30">
                      {showSpecsModal.items && showSpecsModal.items.length > 0 ? (
                        <div className="space-y-2">
                          {showSpecsModal.items.map((item, idx) => {
                            const label = getItemLabel(item, idx);
                            const itemStatus = String(item.item_status || "");
                            return (
                              <div key={item.id ?? idx} className="flex items-center justify-between p-2.5 bg-slate-50 dark:bg-zink-700/40 rounded-lg border border-slate-200 dark:border-zink-500">
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                                  <span className="font-medium">{label}</span>
                                  {itemStatus && (
                                    <span
                                      className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${ITEM_STATUS_CLASSES[itemStatus] ||
                                        "bg-gray-100 text-gray-800 dark:bg-gray-500/20 dark:text-gray-400"
                                        }`}
                                    >
                                      {ITEM_STATUS_LABELS[itemStatus] || itemStatus}
                                    </span>
                                  )}
                                  <span className="text-xs text-slate-500 dark:text-zink-300">
                                    تعداد: {item.quantity || 0}
                                  </span>
                                  <span className="text-xs text-slate-500 dark:text-zink-300">
                                    طول: {item.length || 0}
                                  </span>
                                  <span className="text-xs text-slate-500 dark:text-zink-300">
                                    عرض: {item.width || 0}
                                  </span>
                                  <span className="text-xs text-slate-500 dark:text-zink-300">
                                    قیمت واحد: {Number(item.unit_price || 0).toLocaleString('en-US')}
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setShowItemDetailModal({ invoice: showSpecsModal, item, index: idx })}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-custom-600 dark:text-custom-400 border border-custom-500 dark:border-custom-400 rounded-lg bg-transparent hover:bg-custom-50 dark:hover:bg-custom-900/20 transition-colors duration-200"
                                >
                                  شرح کالا
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-500 dark:text-zink-300">هیچ کالایی ثبت نشده است.</p>
                      )}
                    </div>
                  </div>

                  <div className="text-xs text-black dark:text-zink-400 pt-1 flex flex-wrap gap-3 justify-end">
                    <span>شناسه: {showSpecsModal.id}</span>
                    <span>شناسه مشتری: {showSpecsModal.customer_id}</span>
                  </div>
                </div>
              );
            })()
          )}
        </Modal.Body>
      </Modal>

      {/* ========== مودال شرح کالا (مشخصات کامل یک قلم) ========== */}
      <Modal
        show={!!showItemDetailModal}
        onHide={() => setShowItemDetailModal(null)}
        modal-center="true"
        dialogClassName="w-screen md:w-[46rem]"
      >
        <Modal.Header
          className="flex items-center justify-between p-4 border-b dark:border-zink-500"
          closeButtonClass="transition-all duration-200 ease-linear text-blue-800 dark:text-white hover:text-red-500 dark:hover:text-red-400"
        >
          <Modal.Title className="text-16 font-bold">
            شرح کالا #{showItemDetailModal?.index !== undefined ? (showItemDetailModal.index + 1) : ''}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
          {showItemDetailModal && (() => {
            const { invoice, item, index } = showItemDetailModal;
            const goodsRecord = getGoodsRecord(item.goods_service_id);
            // مشخصات فنی مستقیماً از ProformaInvoiceItemOut خوانده می‌شود و فقط در صورت
            // خالی بودن فیلد سرور، از رمزگشایی کد کالا به‌عنوان جایگزین استفاده می‌شود.
            const decoded = decodeGoodsCode(item.goods_code || goodsRecord?.code);
            const fallbackSheetThickness = (v?: number | null, alt?: string) =>
              v !== undefined && v !== null ? ltrValue(`${(Number(v) / 100).toFixed(2)} ± 0.02`) : alt || "-";
            const panelSpec = {
              panelType: item.panel_type || decoded?.panelType || "-",
              thickness:
                item.panel_thickness !== undefined && item.panel_thickness !== null
                  ? String(item.panel_thickness)
                  : decoded?.thickness || "-",
              topSheet: item.top_sheet_material || decoded?.topSheet || "-",
              bottomSheet: item.bottom_sheet_material || decoded?.bottomSheet || "-",
              topSheetThickness: fallbackSheetThickness(
                item.top_sheet_thickness as any,
                decoded?.topSheetThickness
              ),
              bottomSheetThickness: fallbackSheetThickness(
                item.bottom_sheet_thickness as any,
                decoded?.bottomSheetThickness
              ),
              foamDensity: foamDensityWithTolerance(item.foam_density) || decoded?.foamDensity || "-",
              foamType: item.foam_type || decoded?.foamType || "-",
              topColor: item.top_sheet_color || decoded?.topColor || "-",
              bottomColor: item.bottom_sheet_color || decoded?.bottomColor || "-",
            };
            const itemStatus = String(item.item_status || "");

            return (
              <div className="space-y-4" dir="rtl">
                {/* اطلاعات پایه کالا */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-3 bg-slate-100 dark:bg-zink-600/30 rounded-lg">
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">وضعیت کالا</span>
                    <span
                      className={`font-medium px-2 py-0.5 rounded-full text-xs inline-block w-fit ${ITEM_STATUS_CLASSES[itemStatus] ||
                        'bg-gray-100 text-gray-800 dark:bg-gray-500/20 dark:text-gray-400'
                        }`}
                    >
                      {ITEM_STATUS_LABELS[itemStatus] || itemStatus || '-'}
                    </span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">کد کالا</span>
                    <span className="font-mono font-medium text-slate-700 dark:text-zink-100">{item.goods_code || goodsRecord?.code || '-'}</span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">عنوان کالا</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">{item.goods_title || goodsRecord?.title || '-'}</span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">توضیحات کالا</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">{item.goods_description || '-'}</span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">شماره ردیف</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">{item.line_number || index + 1}</span>
                  </div>
                </div>

                {/* تعداد و ابعاد */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 bg-slate-100 dark:bg-zink-600/30 rounded-lg">
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">تعداد</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">{item.quantity || '-'}</span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">طول</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">{item.length || '-'}</span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">عرض</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">{item.width || '-'}</span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">فی (قیمت واحد)</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">{Number(item.unit_price || 0).toLocaleString('en-US')}</span>
                  </div>
                </div>

                {/* مشخصات فنی پانل (از پاسخ سرور) */}
                {(
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-3 bg-amber-50 dark:bg-zink-700 rounded-lg">
                    <div className="flex flex-col text-right">
                      <span className="text-xs text-black dark:text-zink-400">نوع پانل</span>
                      <span className="font-medium text-slate-700 dark:text-zink-100">{panelSpec.panelType}</span>
                    </div>
                    {/* «نک‌دار رو» و «نک‌دار زیر» همین قلم؛ مستقیم از پاسخ سرور.
                        رکوردهای قدیمی فقط has_notch دارند و همان جایگزین می‌شود. */}
                    <div className="flex flex-col text-right">
                      <span className="text-xs text-black dark:text-zink-400">نک‌دار رو</span>
                      <span className="font-medium text-slate-700 dark:text-zink-100">
                        {notchLabel(item.has_notch_top ?? item.has_notch)}
                      </span>
                    </div>
                    <div className="flex flex-col text-right">
                      <span className="text-xs text-black dark:text-zink-400">نک‌دار زیر</span>
                      <span className="font-medium text-slate-700 dark:text-zink-100">
                        {notchLabel(item.has_notch_bottom ?? item.has_notch)}
                      </span>
                    </div>
                    <div className="flex flex-col text-right">
                      <span className="text-xs text-black dark:text-zink-400">ضخامت پانل</span>
                      <span className="font-medium text-slate-700 dark:text-zink-100">
                        {panelSpec.thickness !== '-' ? `${panelSpec.thickness} سانت` : '-'}
                      </span>
                    </div>
                    <div className="flex flex-col text-right">
                      <span className="text-xs text-black dark:text-zink-400">ورق رو</span>
                      <span className="font-medium text-slate-700 dark:text-zink-100">{panelSpec.topSheet}</span>
                    </div>
                    <div className="flex flex-col text-right">
                      <span className="text-xs text-black dark:text-zink-400">ورق پشت</span>
                      <span className="font-medium text-slate-700 dark:text-zink-100">{panelSpec.bottomSheet}</span>
                    </div>
                    <div className="flex flex-col text-right">
                      <span className="text-xs text-black dark:text-zink-400">ضخامت ورق رو</span>
                      <span dir="ltr" style={{ direction: 'ltr', unicodeBidi: 'bidi-override' }} className="font-medium text-slate-700 dark:text-zink-100 text-right">{panelSpec.topSheetThickness}</span>
                    </div>
                    <div className="flex flex-col text-right">
                      <span className="text-xs text-black dark:text-zink-400">ضخامت ورق پشت</span>
                      <span dir="ltr" style={{ direction: 'ltr', unicodeBidi: 'bidi-override' }} className="font-medium text-slate-700 dark:text-zink-100 text-right">{panelSpec.bottomSheetThickness}</span>
                    </div>
                    <div className="flex flex-col text-right">
                      <span className="text-xs text-black dark:text-zink-400">دانسیته فوم</span>
                      <span dir="ltr" style={{ direction: 'ltr', unicodeBidi: 'bidi-override' }} className="font-medium text-slate-700 dark:text-zink-100 text-right">{panelSpec.foamDensity}</span>
                    </div>
                    <div className="flex flex-col text-right">
                      <span className="text-xs text-black dark:text-zink-400">نوع فوم</span>
                      <span className="font-medium text-slate-700 dark:text-zink-100">{panelSpec.foamType}</span>
                    </div>
                    <div className="flex flex-col text-right">
                      <span className="text-xs text-black dark:text-zink-400">رنگ ورق رو</span>
                      <span className="font-medium text-slate-700 dark:text-zink-100">{panelSpec.topColor}</span>
                    </div>
                    <div className="flex flex-col text-right">
                      <span className="text-xs text-black dark:text-zink-400">رنگ ورق زیر</span>
                      <span className="font-medium text-slate-700 dark:text-zink-100">{panelSpec.bottomColor}</span>
                    </div>
                  </div>
                )}

                {/* هزینه بسته‌بندی، مالیات و مبلغ خالص این قلم */}
                <div className="grid grid-cols-3 gap-3 p-3 bg-emerald-50 dark:bg-zink-700 rounded-lg">
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">هزینه بسته‌بندی</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">
                      {Number(item.packaging_cost || 0).toLocaleString('en-US')} ریال
                    </span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">مالیات بر ارزش‌افزوده</span>
                    <span className="font-medium text-slate-700 dark:text-zink-100">
                      {Number(item.vat_amount || 0).toLocaleString('en-US')} ریال
                    </span>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="text-xs text-black dark:text-zink-400">مبلغ خالص</span>
                    <span className="font-bold text-custom-500 text-base">
                      {Number(item.net_amount || 0).toLocaleString('en-US')} ریال
                    </span>
                  </div>
                </div>

                <div className="text-xs text-black dark:text-zink-400 border-t border-slate-200 dark:border-zink-500 pt-2 mt-2 text-left">
                  شناسه کالا: {item.id} | شناسه پیش‌فاکتور: {invoice.id}
                </div>
              </div>
            );
          })()}
        </Modal.Body>
      </Modal>

      {/* ========== مودال‌های پرداخت و حذف (بدون تغییر) ========== */}
      <Modal
        show={showPaymentsModal !== null}
        onHide={() => {
          setShowPaymentsModal(null);
          setApprovalTarget(null);
          setPayments([]);
        }}
        modal-center="true"
        dialogClassName="w-screen md:w-[40rem]"
      >
        <Modal.Header
          className="flex items-center justify-between p-4 border-b dark:border-zink-500"
          closeButtonClass="transition-all duration-200 ease-linear text-blue-800 dark:text-white hover:text-red-500 dark:hover:text-red-400"
        >
          <Modal.Title className="text-16">پرداخت‌های پیش‌فاکتور</Modal.Title>
          <button
            type="button"
            onClick={() => {
              if (showPaymentsModal) {
                setShowRegisterPayment(showPaymentsModal);
                setPaymentForm(resetPaymentForm());
              }
            }}
            className="btn bg-custom-500 text-white text-xs px-3 py-1.5"
          >
            ثبت پرداخت
          </button>
        </Modal.Header>
        <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
          {paymentsLoading ? (
            <div className="flex justify-center items-center h-32">
              <div className="spinner-border animate-spin inline-block w-8 h-8 border-4 rounded-full border-custom-500 border-t-transparent"></div>
            </div>
          ) : payments.length === 0 ? (
            <p className="text-blue-800 text-center py-6">هیچ پرداختی ثبت نشده است</p>
          ) : (
            payments.map((p) => {
              const isApproving =
                approvalTarget?.paymentId === p.id &&
                approvalTarget?.invoiceId === showPaymentsModal;
              return (
                <div
                  key={p.id}
                  className="rounded-lg border border-slate-200 dark:border-zink-500 p-3 mb-3 text-sm"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">
                        {p.payment_type_name} - {parseFloat(p.amount || "0").toLocaleString("en-US")}
                      </div>
                      <div className="text-xs text-blue-800">تاریخ واریز/سررسید: {p.due_date}</div>
                      {p.receipt_file_name && (
                        <div className="text-xs text-blue-800 mt-1 flex items-center gap-1.5">
                          <span>فایل رسید: {p.receipt_file_name}</span>
                          {p.receipt_url && (
                            <button
                              type="button"
                              onClick={() =>
                                setReceiptPreview({ url: p.receipt_url as string, name: p.receipt_file_name as string })
                              }
                              className="text-custom-500 hover:text-custom-600 underline"
                            >
                              مشاهده فایل
                            </button>
                          )}
                        </div>
                      )}
                      {p.accounting_confirmed && (
                        <div className="text-xs text-green-600 mt-1">
                          ✓ تأیید حسابداری - کد رهگیری: {p.tracking_code}
                          {/* تاریخ و زمان ثبت تأیید حسابداری، بلافاصله بعد از متن تأیید.
                              «تاریخ واریز» جداگانه نمایش داده نمی‌شود چون همان مقدار در
                              سطر «تاریخ واریز/سررسید» بالا دیده می‌شود. */}
                          {accountingConfirmedAtLabel(p) && (
                            <div className="font-semibold text-black dark:text-zink-100">
                              تاریخ و زمان ثبت حسابداری: {accountingConfirmedAtLabel(p)}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {p.accounting_confirmed && (
                        <span className="inline-flex items-center gap-1 text-green-600 text-xs font-medium">
                          <CheckCircle2 className="size-4" /> تأیید شده
                        </span>
                      )}
                      {/* تأیید حسابداری تا وقتی سفارش تسویه نشده همیشه در دسترس است. */}
                      {!p.accounting_confirmed &&
                        mayConfirmAccounting &&
                        !isSettled(paymentsInvoice) && (
                        <button
                          type="button"
                          onClick={() => startApproval(showPaymentsModal!, p.id)}
                          className="text-xs px-2.5 py-1 rounded bg-purple-500 text-white hover:bg-purple-600"
                        >
                          تأیید حسابداری
                        </button>
                      )}
                      {/* ویرایش و حذف پرداختِ تأییدنشده هم به همان قاعده گره خورده است. */}
                      {(isAdmin ||
                        (mayRegisterPayment &&
                          !p.accounting_confirmed &&
                          !isSettled(paymentsInvoice))) && (
                        <>
                          <button
                            type="button"
                            onClick={() => openEditPayment(p)}
                            className="text-yellow-500 hover:text-yellow-700"
                            title="ویرایش"
                          >
                            <Pencil className="size-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              Swal.fire({
                                title: "حذف پرداخت",
                                text: "آیا از حذف این پرداخت اطمینان دارید؟",
                                icon: "warning",
                                showCancelButton: true,
                                confirmButtonText: "بله، حذف کن",
                                cancelButtonText: "لغو",
                              }).then((result) => {
                                if (result.isConfirmed) {
                                  handleDeletePayment(showPaymentsModal!, p.id);
                                }
                              });
                            }}
                            className="text-red-500 hover:text-red-700"
                            title="حذف"
                          >
                            <Trash2 className="size-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {isSalesExpert && p.accounting_confirmed && (
                    <div className="text-xs text-orange-500 mt-2">
                      به علت تایید حسابداری امکان ویرایش وجود ندارد
                    </div>
                  )}

                  {isApproving && approvalStep === "confirm" && (
                    <div className="mt-3 p-3 rounded-lg bg-purple-50 dark:bg-zink-700 text-sm">
                      <p className="mb-2">
                        تأیید می‌کنید که مبلغ{" "}
                        {parseFloat(p.amount || "0").toLocaleString("en-US")} در تاریخ {p.due_date} به
                        حساب کارخانه واریز شده است؟
                      </p>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={confirmApprovalStart}
                          className="btn bg-custom-500 text-white text-xs px-4 py-1.5"
                        >
                          بله
                        </button>
                        <button
                          type="button"
                          onClick={cancelApproval}
                          className="btn bg-slate-200 dark:bg-zink-500 text-xs px-4 py-1.5"
                        >
                          خیر
                        </button>
                      </div>
                    </div>
                  )}

                  {isApproving && approvalStep === "form" && (
                    <div className="mt-3 p-3 rounded-lg bg-purple-50 dark:bg-zink-700 space-y-3">
                      <div>
                        <label className="block text-xs font-medium mb-1">کد رهگیری:</label>
                        <input
                          type="text"
                          className="form-input w-full text-sm"
                          value={approvalForm.tracking_code}
                          onChange={(e) =>
                            setApprovalForm((prev) => ({
                              ...prev,
                              tracking_code: e.target.value,
                            }))
                          }
                        />
                      </div>
                      <ModalData
                        label="تاریخ واریز:"
                        data={approvalForm.deposit_date}
                        setData={(v) =>
                          setApprovalForm((prev) => ({ ...prev, deposit_date: v }))
                        }
                      />
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={submitApproval}
                          className="btn bg-custom-500 text-white text-xs px-4 py-1.5"
                        >
                          ثبت تأیید
                        </button>
                        <button
                          type="button"
                          onClick={cancelApproval}
                          className="btn bg-slate-200 dark:bg-zink-500 text-xs px-4 py-1.5"
                        >
                          انصراف
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={!!receiptPreview}
        onHide={() => setReceiptPreview(null)}
        modal-center="true"
        dialogClassName="w-screen md:w-[32rem]"
      >
        <Modal.Header
          className="flex items-center justify-between p-4 border-b dark:border-zink-500"
          closeButtonClass="transition-all duration-200 ease-linear text-blue-800 dark:text-white hover:text-red-500 dark:hover:text-red-400"
        >
          <Modal.Title className="text-16">{receiptPreview?.name || "فایل رسید"}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4 flex items-center justify-center">
          {receiptPreview && (
            <img
              src={receiptPreview.url}
              alt={receiptPreview.name}
              className="max-w-full max-h-[70vh] rounded-lg object-contain"
            />
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={showRegisterPayment !== null}
        onHide={() => {
          setShowRegisterPayment(null);
          setPaymentForm(resetPaymentForm());
        }}
        modal-center="true"
        dialogClassName="w-screen md:w-[34rem]"
      >
        <Modal.Header
          className="flex items-center justify-between p-4 border-b dark:border-zink-500"
          closeButtonClass="transition-all duration-200 ease-linear text-blue-800 dark:text-white hover:text-red-500 dark:hover:text-red-400"
        >
          <Modal.Title className="text-16">ثبت پرداخت جدید</Modal.Title>
        </Modal.Header>
        <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <div>
              <label className="block text-base font-medium mb-1">نوع پرداخت:</label>
              <Select
                options={paymentTypeSearch.options}
                onInputChange={paymentTypeSearch.onInputChange}
                value={
                  paymentForm.payment_type_id
                    ? {
                      value: paymentForm.payment_type_id,
                      label:
                        paymentTypes.find(
                          (p) => String(p.id) === paymentForm.payment_type_id
                        )?.name || "",
                    }
                    : null
                }
                onChange={(opt: any) =>
                  setPaymentForm((prev) => ({ ...prev, payment_type_id: opt?.value || "" }))
                }
                placeholder="جست‌وجوی نوع پرداخت..."
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                noOptionsMessage={() => "موردی یافت نشد"}
              />
            </div>

            <div>
              <label className="block text-base font-medium mb-1">مبلغ:</label>
              <input
                type="text"
                inputMode="numeric"
                className="form-input w-full"
                value={paymentForm.amount}
                onChange={(e) => {
                  const val = e.target.value.replace(/,/g, "");
                  if (val === "" || /^\d*$/.test(val)) {
                    setPaymentForm((prev) => ({
                      ...prev,
                      amount: val ? Number(val).toLocaleString("en-US") : "",
                    }));
                  }
                }}
                placeholder="۰"
              />
            </div>

            <ModalData
              label="تاریخ سررسید:"
              data={paymentForm.due_date}
              setData={(v) => setPaymentForm((prev) => ({ ...prev, due_date: v }))}
            />

            <div>
              <label className="block text-base font-medium mb-1">فایل رسید:</label>
              <input
                type="file"
                className="form-input w-full"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    if (file.size > 5 * 1024 * 1024) {
                      Swal.fire("خطا", "حجم فایل نباید بیشتر از ۵ مگابایت باشد", "error");
                      e.target.value = "";
                      return;
                    }
                    setPaymentForm((prev) => ({ ...prev, receipt_file: file }));
                  }
                }}
              />
            </div>

            {isAdmin && (
              <>
                <div>
                  <label className="block text-base font-medium mb-1">کد رهگیری:</label>
                  <input
                    type="text"
                    className="form-input w-full"
                    value={paymentForm.tracking_code}
                    onChange={(e) =>
                      setPaymentForm((prev) => ({ ...prev, tracking_code: e.target.value }))
                    }
                  />
                </div>
                <ModalData
                  label="تاریخ واریز:"
                  data={paymentForm.deposit_date}
                  setData={(v) => setPaymentForm((prev) => ({ ...prev, deposit_date: v }))}
                />
              </>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer className="flex justify-center p-4 border-t dark:border-zink-500">
          <button
            type="button"
            onClick={handleRegisterPayment}
            disabled={paymentSubmitting}
            className="btn min-w-[9rem] px-8 py-2.5 mt-2 font-bold text-white bg-custom-500 border-custom-500 hover:bg-custom-600 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {paymentSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="size-4 animate-spin" />
                در حال ثبت...
              </span>
            ) : (
              "ثبت پرداخت"
            )}
          </button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!editPaymentTarget}
        onHide={() => setEditPaymentTarget(null)}
        modal-center="true"
        dialogClassName="w-screen md:w-[34rem]"
      >
        <Modal.Header
          className="flex items-center justify-between p-4 border-b dark:border-zink-500"
          closeButtonClass="transition-all duration-200 ease-linear text-blue-800 dark:text-white hover:text-red-500 dark:hover:text-red-400"
        >
          <Modal.Title className="text-16">ویرایش پرداخت</Modal.Title>
        </Modal.Header>
        <Modal.Body className="max-h-[calc(theme('height.screen')_-_180px)] p-4 overflow-y-auto">
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <div>
              <label className="block text-base font-medium mb-1">نوع پرداخت:</label>
              <Select
                options={paymentTypeSearch.options}
                onInputChange={paymentTypeSearch.onInputChange}
                value={
                  editPaymentForm.payment_type_id
                    ? {
                      value: editPaymentForm.payment_type_id,
                      label:
                        paymentTypes.find(
                          (p) => String(p.id) === editPaymentForm.payment_type_id
                        )?.name || "",
                    }
                    : null
                }
                onChange={(opt: any) =>
                  setEditPaymentForm((prev) => ({ ...prev, payment_type_id: opt?.value || "" }))
                }
                placeholder="جست‌وجوی نوع پرداخت..."
                styles={selectStyles}
                menuPortalTarget={document.body}
                menuPosition="fixed"
                noOptionsMessage={() => "موردی یافت نشد"}
              />
            </div>

            <div>
              <label className="block text-base font-medium mb-1">مبلغ:</label>
              <input
                type="text"
                inputMode="numeric"
                className="form-input w-full"
                value={editPaymentForm.amount}
                onChange={(e) => {
                  const val = e.target.value.replace(/,/g, "");
                  if (val === "" || /^\d*$/.test(val)) {
                    setEditPaymentForm((prev) => ({
                      ...prev,
                      amount: val ? Number(val).toLocaleString("en-US") : "",
                    }));
                  }
                }}
                placeholder="۰"
              />
            </div>

            <ModalData
              label="تاریخ سررسید:"
              data={editPaymentForm.due_date}
              setData={(v) => setEditPaymentForm((prev) => ({ ...prev, due_date: v }))}
            />

            <div>
              <label className="block text-base font-medium mb-1">فایل رسید جدید (اختیاری):</label>
              <input
                type="file"
                className="form-input w-full"
                accept="image/jpeg,image/png,image/webp"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    if (file.size > 5 * 1024 * 1024) {
                      Swal.fire("خطا", "حجم فایل نباید بیشتر از ۵ مگابایت باشد", "error");
                      e.target.value = "";
                      return;
                    }
                    setEditPaymentForm((prev) => ({ ...prev, receipt_file: file }));
                  }
                }}
              />
            </div>

            {isAdmin && (
              <>
                <div className="xl:col-span-2 flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="edit-accounting-confirmed"
                    checked={editPaymentForm.accounting_confirmed}
                    onChange={(e) =>
                      setEditPaymentForm((prev) => ({ ...prev, accounting_confirmed: e.target.checked }))
                    }
                  />
                  <label htmlFor="edit-accounting-confirmed" className="text-base font-medium">
                    تأیید حسابداری
                  </label>
                </div>
                {editPaymentForm.accounting_confirmed && (
                  <>
                    <div>
                      <label className="block text-base font-medium mb-1">کد رهگیری:</label>
                      <input
                        type="text"
                        className="form-input w-full"
                        value={editPaymentForm.tracking_code}
                        onChange={(e) =>
                          setEditPaymentForm((prev) => ({ ...prev, tracking_code: e.target.value }))
                        }
                      />
                    </div>
                    <ModalData
                      label="تاریخ واریز:"
                      data={editPaymentForm.deposit_date}
                      setData={(v) => setEditPaymentForm((prev) => ({ ...prev, deposit_date: v }))}
                    />
                  </>
                )}
              </>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer className="flex justify-center p-4 border-t dark:border-zink-500">
          <button
            type="button"
            onClick={handleUpdatePayment}
            disabled={editPaymentSubmitting}
            className="btn min-w-[9rem] px-8 py-2.5 mt-2 font-bold text-white bg-custom-500 border-custom-500 hover:bg-custom-600 disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {editPaymentSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="size-4 animate-spin" />
                در حال ثبت...
              </span>
            ) : (
              "ذخیره تغییرات"
            )}
          </button>
        </Modal.Footer>
      </Modal>

      <DeleteModal
        show={deleteTargetId !== null}
        onHide={() => setDeleteTargetId(null)}
        onDelete={handleDeleteInvoice}
      />
      {/* سند چاپ: روی صفحه دیده نمی‌شود و فقط هنگام چاپ ظاهر می‌شود. */}
      {printData && <ProformaPrintDocument data={printData} />}
    </React.Fragment>
  );
};

export default Index;
